import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from 'antd';
import {
  CircleCheck, TriangleAlert, CircleX, CircleMinus,
  ChartColumn, Inbox, Clipboard, ChevronRight, Check, Info,
  type LucideIcon,
} from 'lucide-react';
import { api, fmt, API_BASE, getToken } from '../api';
import { CATEGORY_KO } from '../labels';
import DateNav from '../components/DateNav';
import MerchantDetailModal from '../components/MerchantDetailModal';
import { SkeletonStats, SkeletonTable } from '../components/Skeleton';

// 인증 헤더가 필요한 이미지 → fetch로 blob 로드 후 objectURL로 표시.
// 로딩/에러 상태를 명시적으로 표시해서 "엑박"이 안 나오도록.
function AuthImg({ url, alt, style }: { url: string; alt: string; style?: React.CSSProperties }) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let live = true;
    let objectUrl: string | null = null;
    setError(null);
    setSrc(null);
    (async () => {
      try {
        const tok = getToken();
        const r = await fetch(url, {
          headers: tok ? { Authorization: `Bearer ${tok}` } : undefined,
          cache: 'no-store',
        });
        if (!live) return;
        if (!r.ok) { setError(`HTTP ${r.status}`); return; }
        const b = await r.blob();
        if (!live) return;
        if (b.size === 0) { setError('빈 파일'); return; }
        objectUrl = URL.createObjectURL(b);
        setSrc(objectUrl);
      } catch (e: any) {
        if (live) setError(e?.message ?? '로드 실패');
      }
    })();
    return () => { live = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url, retry]);

  const baseStyle: React.CSSProperties = {
    ...style,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f3f4f6', color: 'var(--text-muted)', fontSize: 11, textAlign: 'center',
  };
  if (error) {
    return (
      <div style={baseStyle} title={`${error} · 클릭하면 다시 시도`}
           onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRetry(v => v + 1); }}>
        <span>이미지 로드 실패<br/>({error})<br/><u>다시 시도</u></span>
      </div>
    );
  }
  if (!src) {
    return <div style={baseStyle}><span>불러오는 중…</span></div>;
  }
  return <img src={src} alt={alt} style={style}
              onError={() => setError('디코드 실패')} />;
}

// 라벨 옆 ⓘ 아이콘 — hover·focus 시 지연 없이 커스텀 툴팁 표시.
// .summary .stat 카드에 overflow:hidden 있어 네이티브 title 대신 body 포털로 렌더링.
// position:fixed + viewport 클램프로 화면 밖 이탈 방지. z-index 9999로 표·모달 뒤로 안 밀림.
function InfoTip({ text }: { text: string }) {
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; placement: 'top' | 'bottom' }>({ left: 0, top: 0, placement: 'top' });

  const compute = () => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const tipMaxW = 280;
    const tipEstH = 44; // 대략 2줄 높이. 실제 height 잘못 잡혀도 클램프에서 보정됨.
    const gap = 8;
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = rect.left + rect.width / 2 - tipMaxW / 2;
    left = Math.max(margin, Math.min(vw - tipMaxW - margin, left));
    let placement: 'top' | 'bottom' = 'top';
    let top = rect.top - tipEstH - gap;
    if (top < margin) {
      placement = 'bottom';
      top = rect.bottom + gap;
      if (top + tipEstH > vh - margin) top = Math.max(margin, vh - tipEstH - margin);
    }
    setPos({ left, top, placement });
  };
  const show = () => { compute(); setVisible(true); };
  const hide = () => setVisible(false);

  useEffect(() => {
    if (!visible) return;
    const onScrollOrResize = () => hide();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [visible]);

  return (
    <>
      <span
        ref={anchorRef}
        role="button"
        tabIndex={0}
        aria-label={text}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onKeyDown={(e) => { if (e.key === 'Escape') hide(); }}
        style={{ display: 'inline-flex', marginLeft: 4, verticalAlign: -2, cursor: 'help', outline: 'none' }}
      >
        <Info size={12} style={{ color: 'var(--muted)' }} />
      </span>
      {visible && typeof document !== 'undefined' && createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            maxWidth: 280,
            padding: '7px 10px',
            background: 'rgba(20, 30, 25, 0.94)',
            color: '#fff',
            fontSize: 12,
            lineHeight: 1.5,
            borderRadius: 6,
            boxShadow: '0 6px 18px -8px rgba(0,0,0,.35)',
            zIndex: 9999,
            pointerEvents: 'none',
            whiteSpace: 'normal',
            wordBreak: 'keep-all',
          }}
        >
          {text}
        </div>,
        document.body,
      )}
    </>
  );
}

type Row = {
  mcht_id: string; merchant_name: string; category?: string;
  range_start: string; range_end: string;
  gross_sales: number; net_sales: number; fee_amount: number;
  daily_deduct: number; prev_shortfall: number; today_cap: number;
  settle_amount: number; today_shortfall: number;
  prev_unpaid: number; today_unpaid: number;
  refund_pending?: number;
  merchant_payout: number; principal_repaid: number;
  cumulative_repaid?: number; purchase_amount?: number;
  // 20260909: 카드매출 원금 반환 (엑셀 V) = MIN(net, adjustedTodayCap). CUTOVER 이후 스냅샷만 API가 채움.
  card_principal_repaid?: number;
  // 반환상태 A안(계약 전체 기준)용 — API 제공. 실제 반환실적 = cumulative_repaid − adj_principal_contribution.
  adj_principal_contribution?: number;
  purchase_not_applicable?: number;
  cycle_started_at?: string | null;
  // 3차: 선택 정산일에 반영된 관리자 조정 존재 여부/합계 — '[조정]' 표시용(API 제공, 읽기전용).
  has_date_adjustment?: boolean;
  date_adjustment_amount?: number;
  remaining_days: number; remaining_amount: number;
  merchant_fee_rate?: number;
  fee_rate?: number;
  discount_noti_count: number;
  discount_noti_paid_count: number;
  discount_noti_cancel_count: number;
  discount_noti_gross: number;
  discount_noti_paid_gross: number;
  discount_noti_cancel_gross: number;
  // v14 — 선납 잔액 + 당일 이체
  prepaid_balance_before?: number;
  payment_in_today?: number;
  prepaid_used?: number;
  prepaid_balance_after?: number;
  transfer_in_amount?: number;
  transfer_in_count?: number;
  transfer_out_amount?: number;
  transfer_out_count?: number;
  // 송금 계좌 (가맹점 마스터)
  payout_method?: 'bank' | 'wallet' | string | null;
  payout_bank?: string | null;
  payout_account?: string | null;
  payout_holder?: string | null;
  payout_memo?: string | null;
  // 검색용 부가 필드
  ceo_name?: string | null;
  business_no?: string | null;
  people_id?: string | null;
};

type TransferRow = {
  id: number;
  mcht_id: string;
  merchant_name: string | null;
  direction: 'in' | 'out';
  trx_date: string;
  amount: number;
  memo: string | null;
  ref_settle_date: string | null;
  created_by: string | null;
  created_at: string;
  attachment_count?: number;
};

type AttachmentMeta = {
  id: number;
  filename: string;
  mime: string;
  byte_size: number;
  uploaded_at: string;
  uploaded_by: string | null;
};

const MAX_ATT_BYTES = 5 * 1024 * 1024;
const MAX_ATT_PER_TRANSFER = 3;

// 3차: 환수대기·선납(시작/사용/종료) 4개 컬럼 화면 숨김 플래그.
//   false면 정산 목록 상세 보기에서 해당 th/td를 렌더하지 않는다. DB 컬럼·저장값·계산 로직·데이터 출처는 그대로 보존
//   (엑셀 다운로드 등 후속 기능에서 재사용). 다시 노출하려면 true로만 바꾸면 됨.
const SHOW_LEGACY_COLS = false;

type RepayNoti = {
  ord_num: string; trx_id: string | null; appr_num: string | null;
  trx_at: string; cxl_at: string | null;
  is_cancel: number; settlmnt_cycle: string | null;
  gross_amount: number; fee_amount: number; net_amount: number; fee_rate: number;
  mcht_name: string | null;
};

// 3차: 당일 잔여액 조정/메모 통합 팝업의 조정 이력 행 (GET /merchants/:mid/adjustments).
type AdjRow = {
  id: number; kind: string; effective_date: string; amount: number;
  reason: string | null; period_yyyymm: string | null;
  created_at: string; created_by: string | null;
};
const ADJ_KIND_LABEL: Record<string, string> = {
  remaining: '잔여액 조정', return_daily: '당일 반환 조정',
  shortfall: '전일미차감 조정', device_fee: '단말기 통신비', sale_cancel: '매출취소 조정',
};

type RepayStatus = 'fulfilled' | 'partial' | 'missed' | 'na';
const STATUS_LABEL: Record<RepayStatus, string> = {
  fulfilled: '반환완료',
  partial:   '일부반환',
  missed:    '미반환',
  na:        '비대상',
};
const STATUS_ICON: Record<RepayStatus, LucideIcon> = {
  fulfilled: CircleCheck,
  partial:   TriangleAlert,
  missed:    CircleX,
  na:        CircleMinus,
};

function todayKstStr(): string {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 3600 * 1000);
  return kst.toISOString().slice(0, 10);
}

type SettleMode = 'discount' | 'realtime';

export default function Settlement({ mode = 'discount' }: { mode?: SettleMode } = {}) {
  const [date, setDate] = useState(todayKstStr());
  const [data, setData] = useState<Row[]>([]);
  const [source, setSource] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState<string | null>(null);

  const [repayDetail, setRepayDetail] = useState<Row | null>(null);
  const [repayDetailExpanded, setRepayDetailExpanded] = useState(false);  // 문서 09-01 §8
  const [repayNotis, setRepayNotis] = useState<RepayNoti[] | null>(null);
  const [repayLoading, setRepayLoading] = useState(false);
  const [repayErr, setRepayErr] = useState<string | null>(null);

  // 푸터 "가맹점 매출 (합계)" 클릭 시 — 전체 가맹점의 노티 합계/가맹점별 분해 모달
  const [salesDetailOpen, setSalesDetailOpen] = useState(false);
  const [salesDetailExpanded, setSalesDetailExpanded] = useState(false);  // §8

  // 이체 등록 모달 — direction='in'(가맹점→서포트리 입금) / 'out'(서포트리→가맹점 송금)
  // 20260909: direction='in'은 두 날짜 분리
  //   xferDate = 실제 은행 입금일 (trx_date, 기본 오늘 KST)
  //   xferRefSettleDate = 정산 반영일 (ref_settle_date, 기본 화면 정산기준일 date)
  const [xferModal, setXferModal] = useState<{ row: Row; direction: 'in' | 'out' } | null>(null);
  const [xferAmount, setXferAmount] = useState('');
  const [xferDate, setXferDate] = useState(todayKstStr());
  const [xferRefSettleDate, setXferRefSettleDate] = useState(date);
  const [xferMemo, setXferMemo] = useState('');
  const [xferSaving, setXferSaving] = useState(false);
  const [xferErr, setXferErr] = useState<string | null>(null);
  const [xferFiles, setXferFiles] = useState<File[]>([]);

  // 이체 이력 → 첨부 미리보기 (transfer.id → 메타 배열, 로드 후 캐싱)
  const [attByTransfer, setAttByTransfer] = useState<Record<number, AttachmentMeta[]>>({});
  const [expandedAtt, setExpandedAtt] = useState<Set<number>>(new Set());

  // 이체 이력 모달 (가맹점별)
  const [xferLogMcht, setXferLogMcht] = useState<Row | null>(null);
  const [detailMid, setDetailMid] = useState<string | null>(null);
  const [xferLog, setXferLog] = useState<TransferRow[] | null>(null);
  const [xferLogLoading, setXferLogLoading] = useState(false);

  // 3차: 비대상 숨기기 토글 (화면 표시만 — 데이터·집계·상단 현황 불변). 진입 시 기본 ON.
  const [hideNa, setHideNa] = useState(true);
  // 3차: 당일 잔여액 클릭 → 조정·메모 통합 팝업
  const [adjModal, setAdjModal] = useState<Row | null>(null);
  const [adjHistory, setAdjHistory] = useState<AdjRow[] | null>(null);
  const [adjHistLoading, setAdjHistLoading] = useState(false);
  const [adjSign, setAdjSign] = useState<'+' | '-'>('+');
  const [adjAmount, setAdjAmount] = useState('');
  const [adjReason, setAdjReason] = useState('');
  const [adjSaving, setAdjSaving] = useState(false);
  const [adjErr, setAdjErr] = useState<string | null>(null);
  const [adjMsg, setAdjMsg] = useState<string | null>(null);
  const [adjMerchantMemo, setAdjMerchantMemo] = useState('');
  const [adjMemoSaving, setAdjMemoSaving] = useState(false);
  const [adjMemoMsg, setAdjMemoMsg] = useState<string | null>(null);

  // 일괄 선택 (이체용)
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [batchRunning, setBatchRunning] = useState(false);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);
  // 일괄 이체 최종 확인 모달 상태 & 실행 결과
  const [batchConfirmOpen, setBatchConfirmOpen] = useState(false);
  const [batchResult, setBatchResult] = useState<{
    total: number; ok: number; fail: { mid: string; name: string; error: string }[];
  } | null>(null);

  // 실시간 화면 — 정산방식(계좌/지갑) 필터
  const [methodFilter, setMethodFilter] = useState<'all'|'bank'|'wallet'>('all');
  const [catFilter, setCatFilter] = useState<'all'|'repay'|'normal'|'bad'>('all');  // 문서 09-01 §4
  // 가맹점 검색·행 필터
  const [q, setQ] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all'|'fulfilled'|'partial'|'missed'|'na'>('all');
  const [rowFilter, setRowFilter] = useState<'all'|'has_payout'|'has_shortfall'|'expired'>('all');
  // 표 컬럼 표시 모드 — basic(핵심만) / detail(전체). 진입 시 항상 상세 보기로 시작(이전 간편 보기 선택을 복원하지 않음).
  const [viewMode, setViewMode] = useState<'basic'|'detail'>('detail');
  const setViewModePersist = (m: 'basic'|'detail') => setViewMode(m);
  const isBasic = viewMode === 'basic';

  const [reloadTick, setReloadTick] = useState(0);
  const reload = () => setReloadTick(t => t + 1);

  // 상세 화면 확대 조회 모달 (요청서 §9: 상세 컬럼이 많아 큰 팝업)
  const [tableExpandOpen, setTableExpandOpen] = useState(false);

  // 선택 후보 = merchant_payout > 0인 행만 (실제 송금할 금액이 있는 행)
  const payoutCandidates = (rs: Row[]) => rs.filter(r => r.merchant_payout > 0);

  const toggleSelect = (mid: string) => {
    setSelected(prev => {
      const ns = new Set(prev);
      if (ns.has(mid)) ns.delete(mid); else ns.add(mid);
      return ns;
    });
  };
  const toggleSelectAll = () => {
    const candidates = payoutCandidates(data);
    if (candidates.every(r => selected.has(r.mcht_id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(candidates.map(r => r.mcht_id)));
    }
  };

  const selectedRows = data.filter(r => selected.has(r.mcht_id));
  const selectedSum = selectedRows.reduce((s, r) => s + (r.merchant_payout || 0), 0);

  // 계좌·금액 클립보드 복사 (탭 구분 — 엑셀/메모장 호환)
  const copyAccounts = async () => {
    if (selectedRows.length === 0) { setCopyMsg('선택된 가맹점이 없습니다.'); return; }
    const lines = ['가맹점명\t은행\t계좌번호\t예금주\t금액\t통장표시'];
    for (const r of selectedRows) {
      lines.push([
        r.merchant_name,
        r.payout_bank ?? '',
        r.payout_account ?? '',
        r.payout_holder ?? '',
        r.merchant_payout.toLocaleString('ko-KR'),
        r.payout_memo ?? '',
      ].join('\t'));
    }
    const total = '합계\t\t\t\t' + selectedSum.toLocaleString('ko-KR');
    const text = lines.concat([total]).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopyMsg(`${selectedRows.length}개 가맹점 계좌·금액 복사됨 (탭 구분, 엑셀 붙여넣기 가능)`);
    } catch {
      setCopyMsg('클립보드 접근 실패 — 브라우저 권한 확인');
    }
    setTimeout(() => setCopyMsg(null), 4000);
  };

  // 일괄 이체 완료 처리 — 확인 모달 → 실행 → 결과 요약.
  const openBatchConfirm = () => {
    if (selectedRows.length === 0) return;
    setBatchConfirmOpen(true);
  };
  const runBatchTransferOut = async () => {
    if (selectedRows.length === 0) return;
    setBatchRunning(true);
    const fail: { mid: string; name: string; error: string }[] = [];
    let ok = 0;
    for (const r of selectedRows) {
      try {
        await api('POST', '/transfers', {
          mcht_id: r.mcht_id,
          direction: 'out',
          trx_date: date,
          amount: r.merchant_payout,
          memo: '일괄 이체 완료',
          ref_settle_date: date,
        });
        ok++;
      } catch (e: any) {
        fail.push({ mid: r.mcht_id, name: r.merchant_name, error: e?.message ?? '알 수 없음' });
      }
    }
    setBatchRunning(false);
    setBatchConfirmOpen(false);
    setBatchResult({ total: selectedRows.length, ok, fail });
    setSelected(new Set());
    reload();
  };

  const openXfer = (row: Row, direction: 'in' | 'out') => {
    setXferModal({ row, direction });
    setXferAmount(direction === 'out' ? String(Math.max(0, row.merchant_payout)) : '');
    // in: 은행 입금일 = 오늘 KST, 정산 반영일 = 화면 정산기준일 (D)
    // out: 이체 일자 = 화면 정산기준일 (D) — 기존 동작 유지
    setXferDate(direction === 'in' ? todayKstStr() : date);
    setXferRefSettleDate(date);
    setXferMemo('');
    setXferErr(null);
    setXferFiles([]);
  };
  const closeXfer = () => { setXferModal(null); setXferErr(null); setXferFiles([]); };

  // 첨부 파일 사전 검증 (프론트에서 즉시 반환) — 최종 검증은 서버 매직바이트로 이중 확인.
  const validateAttachment = (f: File): string | null => {
    if (f.size > MAX_ATT_BYTES) return `${f.name}: 파일 크기 5MB 초과`;
    if (f.type !== 'image/jpeg' && f.type !== 'image/png') return `${f.name}: JPG/PNG만 첨부 가능`;
    return null;
  };
  const onPickFiles = (list: FileList | null) => {
    if (!list) return;
    const incoming = Array.from(list);
    const total = xferFiles.length + incoming.length;
    if (total > MAX_ATT_PER_TRANSFER) {
      setXferErr(`첨부는 최대 ${MAX_ATT_PER_TRANSFER}장까지 가능합니다 (현재 ${xferFiles.length}장)`);
      return;
    }
    for (const f of incoming) {
      const err = validateAttachment(f);
      if (err) { setXferErr(err); return; }
    }
    setXferErr(null);
    setXferFiles(prev => [...prev, ...incoming]);
  };
  const removePickedFile = (idx: number) => {
    setXferFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const submitXfer = async () => {
    if (!xferModal) return;
    const amt = Math.round(Number(xferAmount));
    if (!Number.isFinite(amt) || amt <= 0) { setXferErr('금액은 양수여야 합니다.'); return; }
    setXferSaving(true); setXferErr(null);
    try {
      const created = await api<{ ok: true; id: number }>('POST', '/transfers', {
        mcht_id: xferModal.row.mcht_id,
        direction: xferModal.direction,
        trx_date: xferDate,
        amount: amt,
        memo: xferMemo || null,
        // in: 정산 반영일 필수 전달 (API가 CUTOVER·잔여 검증에 사용)
        // out: 이체가 어느 정산일 잉여를 송금하는지 명시 (기존 동작 유지)
        ref_settle_date: xferModal.direction === 'in' ? xferRefSettleDate : date,
      });
      // 이체 등록 성공 후 첨부 순차 업로드 — 실패해도 이체 자체는 유지 (사용자가 재첨부 가능).
      if (xferFiles.length > 0 && created?.id) {
        const failed: string[] = [];
        for (const f of xferFiles) {
          try {
            const fd = new FormData();
            fd.append('file', f);
            await api(`POST`, `/transfers/${created.id}/attachments`, fd);
          } catch (e: any) {
            failed.push(`${f.name} (${e?.message ?? '오류'})`);
          }
        }
        if (failed.length > 0) {
          setXferErr(`이체는 등록되었으나 일부 첨부 실패: ${failed.join(' · ')}`);
          setXferSaving(false);
          return; // 모달 유지 — 사용자에게 실패 알림
        }
      }
      closeXfer();
      reload();
    } catch (e: any) {
      // 서버가 친화 오류 문구를 body.message로 내려주면 그대로 사용자에게 표시.
      // (e.message는 error 코드 그대로, e.body.message는 사용자 안내용)
      const friendly =
        (e && e.body && typeof e.body.message === 'string' && e.body.message.length > 0)
          ? e.body.message
          : (e?.message ?? '저장 실패');
      setXferErr(friendly);
    } finally { setXferSaving(false); }
  };

  // 이체 이력 행 클릭 시 첨부 메타 lazy load.
  const toggleAttachments = async (transferId: number) => {
    if (expandedAtt.has(transferId)) {
      setExpandedAtt(prev => { const s = new Set(prev); s.delete(transferId); return s; });
      return;
    }
    if (!attByTransfer[transferId]) {
      try {
        const r = await api<{ data: AttachmentMeta[] }>('GET', `/transfers/${transferId}/attachments`);
        setAttByTransfer(prev => ({ ...prev, [transferId]: r.data ?? [] }));
      } catch {
        setAttByTransfer(prev => ({ ...prev, [transferId]: [] }));
      }
    }
    setExpandedAtt(prev => { const s = new Set(prev); s.add(transferId); return s; });
  };

  const deleteAttachment = async (transferId: number, aid: number) => {
    if (!confirm('이 증빙 파일을 삭제하시겠습니까?')) return;
    try {
      await api('DELETE', `/transfers/${transferId}/attachments/${aid}`);
      setAttByTransfer(prev => ({
        ...prev,
        [transferId]: (prev[transferId] ?? []).filter(a => a.id !== aid),
      }));
      if (xferLogMcht) await openXferLog(xferLogMcht);
    } catch (e: any) { alert(e?.message ?? '삭제 실패'); }
  };

  const openXferLog = async (row: Row) => {
    setXferLogMcht(row);
    setXferLog(null);
    setXferLogLoading(true);
    try {
      const r = await api<{ data: TransferRow[] }>(
        'GET', `/transfers?mcht_id=${encodeURIComponent(row.mcht_id)}&limit=500`
      );
      setXferLog(r.data ?? []);
    } catch { setXferLog([]); }
    finally { setXferLogLoading(false); }
  };
  const closeXferLog = () => { setXferLogMcht(null); setXferLog(null); };

  const deleteXfer = async (id: number) => {
    if (!confirm('이 이체 기록을 삭제하시겠습니까? (in이면 영향 일자 정산이 자동 재계산됩니다)')) return;
    try {
      await api('DELETE', `/transfers/${id}`);
      if (xferLogMcht) await openXferLog(xferLogMcht);
      reload();
    } catch (e: any) { alert(e?.message ?? '삭제 실패'); }
  };

  const openRepayDetail = async (r: Row) => {
    setRepayDetail(r);
    setRepayNotis(null);
    setRepayErr(null);
    setRepayLoading(true);
    try {
      const params = new URLSearchParams({
        mcht_id: r.mcht_id,
        from: r.range_start,
        to: r.range_end,
        noti_kind: 'merchant_discount',
        exclude_test: '1',
        limit: '500',
        offset: '0',
      });
      const res = await api<{ total: number; data: RepayNoti[] }>(
        'GET', `/notifications/list?${params}`
      );
      setRepayNotis(res.data ?? []);
    } catch (e: any) {
      setRepayErr(e?.message ?? '노티 조회 실패');
    } finally {
      setRepayLoading(false);
    }
  };
  const closeRepayDetail = () => {
    setRepayDetail(null);
    setRepayNotis(null);
    setRepayErr(null);
  };

  // 3차: 당일 잔여액 조정·메모 통합 팝업 ----------------------------------------
  const openAdjModal = async (r: Row) => {
    setAdjModal(r);
    setAdjSign('+'); setAdjAmount(''); setAdjReason('');
    setAdjErr(null); setAdjMsg(null); setAdjMemoMsg(null);
    setAdjMerchantMemo('');
    setAdjHistory(null); setAdjHistLoading(true);
    try {
      // 기존 조정 이력 재사용 (처리일·구분·금액·사유·담당자)
      const res = await api<{ data: AdjRow[] }>('GET', `/merchants/${encodeURIComponent(r.mcht_id)}/adjustments`);
      setAdjHistory(res.data ?? []);
    } catch (e: any) {
      setAdjErr(e?.message ?? '조정 이력 조회 실패'); setAdjHistory([]);
    } finally {
      setAdjHistLoading(false);
    }
    try {
      // 기존 가맹점 관리 메모 재사용 (조정 사유와 별개)
      const m = await api<{ admin_memo?: string | null }>('GET', `/merchants/${encodeURIComponent(r.mcht_id)}`);
      setAdjMerchantMemo(m?.admin_memo ?? '');
    } catch { /* 메모 로드 실패는 치명적 아님 */ }
  };
  const closeAdjModal = () => {
    setAdjModal(null); setAdjHistory(null); setAdjErr(null); setAdjMsg(null); setAdjMemoMsg(null);
  };
  // 조정 전/후 미리보기 (당일 잔여액은 0 미만으로 내려가지 않음)
  const adjPreview = (): { before: number; delta: number; after: number } => {
    const before = Number(adjModal?.remaining_amount ?? 0);
    const amt = Math.round(Number(adjAmount));
    if (!Number.isFinite(amt) || amt <= 0) return { before, delta: 0, after: before };
    const delta = adjSign === '+' ? amt : -amt;
    return { before, delta, after: Math.max(0, before + delta) };
  };
  // 잔여액 조정 저장 — 기존 merchant_adjustments(kind='remaining') 저장 구조 재사용. 신규 계산체계 없음.
  const submitAdj = async () => {
    if (!adjModal || adjSaving) return;  // 중복 클릭 방지
    const amt = Math.round(Number(adjAmount));
    if (!Number.isFinite(amt) || amt <= 0) { setAdjErr('조정 금액은 양수여야 합니다.'); return; }
    if (!adjReason.trim()) { setAdjErr('조정 사유를 입력하세요.'); return; }
    // 미래 정산일은 실제 정산이 없으므로 이 팝업에서 조정 확정 처리하지 않음
    const todayKst = new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
    if (date > todayKst) { setAdjErr('아직 정산이 없는 미래 정산일은 잔여액을 조정할 수 없습니다.'); return; }
    setAdjSaving(true); setAdjErr(null); setAdjMsg(null);
    try {
      const signed = adjSign === '+' ? amt : -amt;
      const resp = await api<{ ok: true; id: number | null; recomputed?: boolean; recompute_skipped?: string | null }>(
        'POST', `/merchants/${encodeURIComponent(adjModal.mcht_id)}/adjustments`,
        { kind: 'remaining', effective_date: date, amount: signed, reason: adjReason.trim() },
      );
      // B안: 조정 저장과 해당 정산일 재계산은 서버에서 원자 반영. 후속 정산 존재·반영 실패 시 서버가 저장 자체를 거부(catch로 안내).
      if (resp.recomputed) {
        setAdjMsg('잔여액 조정이 저장되고 당일 잔여액에 즉시 반영되었습니다. (관리자 조정은 실제 반환실적 아님)');
      } else if (resp.recompute_skipped === 'future_date') {
        setAdjMsg('조정은 기록되었습니다. 미래 정산일이라 지금은 잔여액에 반영하지 않습니다.');
      } else {
        setAdjMsg('잔여액 조정이 저장되었습니다. (관리자 조정은 실제 반환실적 아님)');
      }
      setAdjAmount(''); setAdjReason('');
      const res = await api<{ data: AdjRow[] }>('GET', `/merchants/${encodeURIComponent(adjModal.mcht_id)}/adjustments`);
      setAdjHistory(res.data ?? []);
      reload();  // 목록 재조회 — 재계산된 당일 잔여액/조정 표시 반영
    } catch (e: any) {
      const serverMsg = (e?.body && typeof e.body === 'object' && 'message' in e.body) ? String(e.body.message) : null;
      setAdjErr(serverMsg ?? e?.message ?? '조정 저장 실패 (기존 금액 유지)');
    } finally {
      setAdjSaving(false);
    }
  };
  // 관리자 메모만 저장 — 가맹점 관리 메모(admin_memo) 재사용. 금액·잔여액 불변.
  const submitAdjMemo = async () => {
    if (!adjModal || adjMemoSaving) return;
    setAdjMemoSaving(true); setAdjMemoMsg(null); setAdjErr(null);
    try {
      await api('PATCH', `/merchants/${encodeURIComponent(adjModal.mcht_id)}/memo`, { memo: adjMerchantMemo });
      setAdjMemoMsg('메모가 저장되었습니다. (당일 잔여액은 변경되지 않음)');
    } catch (e: any) {
      setAdjErr(e?.message ?? '메모 저장 실패');
    } finally {
      setAdjMemoSaving(false);
    }
  };

  const runBackfill = async () => {
    if (backfilling) return;
    Modal.confirm({
      title: '누락 정산 일괄 반영',
      width: 520,
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          운영 결제 수신내역이 있는 모든 일자에 대해 정산을 <b>일괄 생성/갱신</b>합니다.
          <div style={{ marginTop: 8, padding: '8px 10px', background: '#fef3c7', color: '#854d0e', borderRadius: 6, fontSize: 12 }}>
            직전일 누적값에 의존하므로 일자 순서대로 처리됩니다. 신규 가맹점·과거 기간이 포함된 경우 시간이 걸릴 수 있습니다.
          </div>
        </div>
      ),
      okText: '일괄 반영 실행',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        setBackfilling(true);
        setBackfillMsg('반영 중…');
        try {
          const r = await api<{
            from: string; to: string; days_processed: number; merchants: number;
            per_date: Array<{ date: string; merchants: number }>;
          }>('POST', '/settlements/backfill', {});
          setBackfillMsg(`완료 — ${r.from} ~ ${r.to}, ${r.days_processed}일치, 각 일자 ${r.merchants}개 가맹점`);
          Modal.success({
            title: '누락 정산 일괄 반영 완료',
            content: (
              <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                <div><b>기간:</b> {r.from} ~ {r.to}</div>
                <div><b>처리 일자:</b> {r.days_processed.toLocaleString('ko-KR')}일</div>
                <div><b>각 일자 가맹점 수:</b> {r.merchants.toLocaleString('ko-KR')}개</div>
              </div>
            ),
            okText: '확인',
          });
          reload();
        } catch (e: any) {
          setBackfillMsg(`오류: ${e?.message ?? '알 수 없음'}`);
          Modal.error({ title: '일괄 반영 오류', content: e?.message ?? '알 수 없음', okText: '확인' });
        } finally {
          setBackfilling(false);
        }
      },
    });
  };

  useEffect(() => {
    let active = true;
    setErr(null); setLoading(true);
    (async () => {
      try {
        const r = await api<{ date: string; source: string; data: Row[] }>('GET', `/settlements/${date}?mode=${mode}`);
        if (!active) return;
        setData(r.data ?? []);
        setSource(r.source);
      } catch (e: any) {
        if (active) setErr(e?.message ?? '조회 실패');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [date, mode, reloadTick]);

  // 실시간 모드에서만 정산방식 필터 적용. payout_method null/누락은 'bank'로 간주(기본값).
  const methodOf = (r: Row): 'bank' | 'wallet' =>
    (r.payout_method === 'wallet' ? 'wallet' : 'bank');

  // 카드매출 원금 반환 (엑셀 V). CUTOVER 이후 API가 card_principal_repaid로 채움. 미제공 시 principal_repaid로 fallback.
  const cardRepaid = (r: Row): number =>
    (typeof r.card_principal_repaid === 'number' ? r.card_principal_repaid : (r.principal_repaid ?? 0));

  // 반환상태 계산 — 반환 목적 입금 반영 후 남은 카드 회수 목표(today_shortfall) 기준.
  // 20260909: 새 semantics
  //   today_cap = 0 이지만 반환 입금으로 회수 완료된 케이스는 fulfilled로 처리 (principal_repaid 또는 payment_in_today > 0).
  //   today_shortfall > 0 이면 아직 미회수분 존재 → partial/missed.
  const rowStatus = (r: Row): RepayStatus => {
    const ts = Number(r.today_shortfall ?? 0);
    const pr = Number(r.principal_repaid ?? 0);
    const tc = Number(r.today_cap ?? 0);
    if (ts > 0) return pr > 0 ? 'partial' : 'missed';
    if (tc > 0 || pr > 0) return 'fulfilled';
    return 'na';
  };

  // 실제 반환실적 (계약 전체 기준, A안) = 카드매출 원금반환 + 은행 반환목적입금
  //   = 누적회수액(cumulative_repaid) − 관리자 조정 기여분(adj_principal_contribution).
  // 관리자 단순조정(미차감 0처리+잔여 가산, 소송비 가산 등)은 실제 반환실적으로 인정하지 않는다.
  const realRepaid = (r: Row): number =>
    Math.max(0, Number(r.cumulative_repaid ?? 0) - Number(r.adj_principal_contribution ?? 0));

  // 정산 목록의 반환상태 — 계약 전체 원금 기준(A안). 상단 '당일 반환 현황'(rowStatus, 당일 기준)과 분리.
  //   비대상: 반환 계약 대상 아님(상환 카테고리 아님 또는 매입 비적용 플래그)
  //   반환완료: 당일 잔여액 0원으로 해소
  //   일부반환: 실제 반환실적 > 0 이고 잔여 원금 존재
  //   미반환: 실제 반환실적 0 (관리자 조정만 있는 경우 포함)
  const contractStatus = (r: Row): RepayStatus => {
    const cat = r.category ?? 'normal';
    if (cat !== 'repay' || Number(r.purchase_not_applicable ?? 0) === 1) return 'na';
    if (Number(r.remaining_amount ?? 0) <= 0) return 'fulfilled';
    return realRepaid(r) > 0 ? 'partial' : 'missed';
  };

  // 검색 매칭 — 가맹점명·MID·대표자명·사업자등록번호(하이픈 무시)·피플 ID.
  const matchQuery = (r: Row): boolean => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    const qDigits = s.replace(/\D/g, '');
    const bnoDigits = (r.business_no ?? '').replace(/\D/g, '');
    return r.mcht_id.toLowerCase().includes(s)
        || r.merchant_name.toLowerCase().includes(s)
        || (r.ceo_name?.toLowerCase().includes(s) ?? false)
        || (r.business_no?.toLowerCase().includes(s) ?? false)
        || (r.people_id?.toLowerCase().includes(s) ?? false)
        || (qDigits.length >= 3 && bnoDigits.includes(qDigits));
  };

  const viewData = data.filter(r => {
    if (mode === 'realtime' && methodFilter !== 'all' && methodOf(r) !== methodFilter) return false;
    if (catFilter !== 'all' && (r.category ?? 'normal') !== catFilter) return false;
    if (!matchQuery(r)) return false;
    if (mode === 'discount' && statusFilter !== 'all' && contractStatus(r) !== statusFilter) return false;
    // 3차: 비대상 숨기기 토글 — 화면 표시만 제외(데이터·집계·상단 현황 불변). 계약 전체 기준(contractStatus) 사용.
    //   단, 사용자가 상태 필터에서 '비대상'을 직접 고른 경우엔 숨기지 않음(기본 ON이라 빈 목록이 되는 것 방지).
    if (mode === 'discount' && hideNa && statusFilter !== 'na' && contractStatus(r) === 'na') return false;
    if (rowFilter === 'has_payout' && r.merchant_payout <= 0) return false;
    if (rowFilter === 'has_shortfall' && r.today_shortfall <= 0) return false;
    if (rowFilter === 'expired' && !(r.remaining_days < 0)) return false;
    return true;
  });

  const sum = (k: keyof Row): number => viewData.reduce((s, r) => s + (Number((r as any)[k]) || 0), 0);
  const sumAll = (k: keyof Row): number => data.reduce((s, r) => s + (Number((r as any)[k]) || 0), 0);
  const isFiltered = viewData.length !== data.length;
  const methodCount = (m: 'bank'|'wallet'): number => data.reduce((n, r) => n + (methodOf(r) === m ? 1 : 0), 0);

  // 목록 표시·목록 필터용 = 계약 전체 기준(A안). 상단 '당일 반환 현황' 카드만 rowStatus(당일 기준) 사용.
  const repayStatus = (r: Row): RepayStatus => contractStatus(r);
  const statusCount = (s: RepayStatus): number => viewData.reduce((n, r) => n + (rowStatus(r) === s ? 1 : 0), 0);

  const range = useMemo(() => data[0] ? `${data[0].range_start} ~ ${data[0].range_end}` : '-', [data]);
  const isWeekendMerge = data[0] && data[0].range_start !== data[0].range_end;
  const sourceLabel = source === 'snapshot' ? '확정 스냅샷' : source === 'live' ? '실시간 계산' : source === 'mixed' ? '혼합 (스냅샷 + 실시간)' : '';

  const feeRateOf = (r: Row): number | undefined => r.merchant_fee_rate ?? r.fee_rate;

  return (
    <div>
      <header className="page-header">
        <div>
          <h2 className="page-title">{mode === 'realtime' ? '실시간 이체정산 (일반 가맹점)' : '할인매매 이체정산 (반환 가맹점)'}</h2>
          <p className="page-sub">
            정산기준일 D의 정산은 <b>전날(D-1)</b>의 할인매매 노티만 가지고 계산합니다. 가맹점이 미납분을 송금하면 "입금 등록" → 누적 미납 차감 + 잉여는 선납 잔액. 매출 잉여를 가맹점에 송금할 때는 "이체 완료" 처리(로그).
          </p>
        </div>
        <div className="page-actions">
          {mode === 'discount' && (
            <button onClick={runBackfill} disabled={backfilling} className="filter-warn-btn">
              {backfilling ? '반영 중…' : '누락 정산 일괄 반영'}
            </button>
          )}
        </div>
      </header>

      <details className="card help-block">
        <summary>ⓘ 정산 화면 안내 — 용어 설명</summary>
        <div className="help-body">
          <h4>1. 정산 기준일과 매출집계 범위</h4>
          <ul>
            <li><b>정산기준일</b>: 화면 상단 날짜 선택에서 고른 일자(KST). 이 날 기준으로 {mode === 'discount' ? '회수·정산을 ' : '정산을 '}산정.</li>
            <li><b>매출집계 범위</b>: 정산기준일 D의 매출집계는 항상 <b>전날 D-1</b>의 {mode === 'discount' ? '할인매매정산 결제 수신내역' : '실시간 결제 수신내역'}만 사용. 예) 5/17 정산 → 5/16 매출 기반.</li>
          </ul>

          {mode === 'discount' && (
            <>
              <h4>2. 회수(원금 반환) 흐름</h4>
              <ul>
                <li><b>일차감액</b> = 매입액 ÷ 약정 기간(일). 매일 정해진 회수 목표 금액.</li>
                <li><b>전일미차감</b> = 어제 매출이 일차감액보다 부족해 못 회수한 누적분.</li>
                <li><b>당일 반환액(캡)</b> = 일차감액 + 전일미차감. 오늘 회수 가능한 상한.</li>
                <li><b>정산금</b> = 총지급액 − 당일 반환액. 양수면 가맹점에 지급, 음수면 회수 부족.</li>
                <li><b>당일미차감액</b> = 정산금이 음수일 때 그 절대값(다음날 전일미차감으로 이월).</li>
                <li><b>원금 반환</b> = 당일 실제 회수된 금액 = 당일 반환액 − 당일미차감액. <b>전액 할인매매정산 결제 수신내역 매출에서 회수</b>. 결제 수신내역 0건 → 회수 0.</li>
              </ul>
              <div className="callout">
                <b>회수 검증 포인트</b> — 각 행의 "할인매매정산 결제 수신내역" 컬럼에 표시된 건수/매출이 0이면 그 가맹점의 가맹점 매출·원금 반환도 0이 정상. 매출이 있는데 결제 수신내역 0이면 원천 누락 의심.
              </div>
            </>
          )}

          <h4>{mode === 'discount' ? '3. 가맹점 지급 흐름' : '2. 가맹점 지급 흐름'}</h4>
          <ul>
            <li>
              <b>가맹점 매출</b> = {mode === 'discount'
                ? <><b>할인매매정산 결제 수신내역</b> 매출 합(취소는 음수). 실시간 결제와 테스트는 정산 입력에서 제외.</>
                : <><b>실시간 결제 수신내역</b> 매출 합(취소는 음수). 할인매매정산 수신과 테스트는 정산 입력에서 제외.</>}
            </li>
            <li><b>총지급액</b> = 가맹점 매출 − 수수료.</li>
            {mode === 'discount' && <li><b>전일미지급</b> = 어제 정산금이 1만원 미만이라 이월된 금액.</li>}
            {mode === 'discount' && <li><b>당일미지급</b> = 오늘 정산금이 1만원 미만이라 이월(다음날 합산).</li>}
            <li><b>가맹점 정산</b> = 실제로 가맹점 계좌로 송금할 금액{mode === 'discount' ? '(1만원 이상일 때)' : ''}.</li>
          </ul>

          {mode === 'discount' && (
            <>
              <h4>4. 잔여 / 상태</h4>
              <ul>
                <li><b>잔여기간</b> = 약정 종료까지 남은 일수.</li>
                <li><b>당일 잔여액</b> = 매입액 − 누적 회수액 − 당일 원금 반환. 0이 되면 회수 완료.</li>
                <li><b>분류</b>: 반환=반환 진행 중 / 일반=반환금 없음 / 악성=장기 미회수.</li>
              </ul>
            </>
          )}

          {mode === 'realtime' && (
            <>
              <h4>3. 실시간 정산의 특성</h4>
              <ul>
                <li>실시간 정산은 <b>회수(원금 반환) 개념이 없음</b>. 총지급액이 곧 가맹점 정산.</li>
                <li>정산방식은 가맹점 마스터의 <b>계좌(bank) / 지갑(wallet)</b> 구분을 그대로 사용.</li>
              </ul>
            </>
          )}

          <h4>{mode === 'discount' ? '5.' : '4.'} 데이터 출처 표시</h4>
          <ul>
            <li><b>확정 스냅샷</b>: DB에 저장된 확정 정산 결과(이미 계산되어 있음).</li>
            <li><b>실시간 계산</b>: 이 조회 요청에 맞춰 즉석 계산(첫 조회 시).</li>
            <li><b>혼합</b>: 일부는 스냅샷, 일부는 실시간. 신규 가맹점이 끼면 발생.</li>
            <li>0원 표시의 의미: <b>매출 없음</b>(실제 매출 0) vs <b>집계 전</b>(오늘 아직 결제 수신내역 미도착) vs <b>데이터 반영 대기</b>(수신은 됐으나 정산 스냅샷 갱신 전) — 각 행의 "결제 건수" 컬럼과 함께 판단.</li>
          </ul>
        </div>
      </details>

      <div className="card filter-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <DateNav date={date} onChange={setDate} />
          <div className="range-info">
            <span title="정산을 산정하는 기준일자 기준, 매출 집계 대상 거래일 범위. 평일=당일, 월요일=금/토/일 3일 합산.">매출집계 범위</span> · <b>{range}</b>{isWeekendMerge ? ' (주말 합산)' : ''}
            {source && <span className={`source-tag ${source}`} title={source === 'snapshot' ? 'DB에 저장된 확정 결과' : source === 'live' ? '이번 요청에 맞춰 즉석 계산' : '일부 스냅샷 + 일부 실시간 계산 혼합'}>{sourceLabel}</span>}
          </div>
          {mode === 'realtime' && (
            <div style={{ display: 'inline-flex', gap: 4, marginLeft: 'auto' }}>
              <span style={{ fontSize: 12, color: 'var(--muted)', alignSelf: 'center', marginRight: 6 }}>정산방식</span>
              {([
                ['all', `전체 (${data.length})`],
                ['bank', `계좌 (${methodCount('bank')})`],
                ['wallet', `지갑 (${methodCount('wallet')})`],
              ] as const).map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setMethodFilter(v)}
                  className={methodFilter === v ? 'filter-primary' : 'filter-ghost'}
                  style={{ fontSize: 12, padding: '4px 10px' }}
                >{label}</button>
              ))}
            </div>
          )}
        </div>
        {/* 가맹점 검색 + 행 필터 */}
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <input value={q} onChange={e => setQ(e.target.value)}
                 placeholder="가맹점명 · 대표자명 · MID · 사업자등록번호 검색"
                 style={{ flex: 1, minWidth: 260, padding: '6px 10px' }} />
          {/* 문서 09-01 §4: 고객분류 필터 */}
          <div style={{ display: 'inline-flex', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--muted)', alignSelf: 'center', marginRight: 4 }}>고객분류</span>
            {([
              ['all', `전체 (${data.length})`],
              ['repay', `반환 (${data.filter(r => (r.category ?? '') === 'repay').length})`],
              ['normal', `일반 (${data.filter(r => (r.category ?? '') === 'normal').length})`],
              ['bad', `악성 (${data.filter(r => (r.category ?? '') === 'bad').length})`],
            ] as const).map(([v, label]) => (
              <button key={v}
                      onClick={() => setCatFilter(v)}
                      className={catFilter === v ? 'filter-primary' : 'filter-ghost'}
                      style={{ fontSize: 12, padding: '4px 10px' }}>{label}</button>
            ))}
          </div>
          {mode === 'discount' && (
            <div style={{ display: 'inline-flex', gap: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--muted)', alignSelf: 'center', marginRight: 4 }}>반환상태</span>
              {(() => {
                const cntFulfilled = data.filter(r => contractStatus(r) === 'fulfilled').length;
                const cntPartial = data.filter(r => contractStatus(r) === 'partial').length;
                const cntMissed = data.filter(r => contractStatus(r) === 'missed').length;
                const cntNa = data.filter(r => contractStatus(r) === 'na').length;
                const opts = [
                  ['all', `전체 (${data.length})`],
                  ['fulfilled', `반환완료 (${cntFulfilled})`],
                  ['partial', `일부반환 (${cntPartial})`],
                  ['missed', `미반환 (${cntMissed})`],
                  ['na', `비대상 (${cntNa})`],
                ] as const;
                return opts.map(([v, label]) => (
                  <button key={v} onClick={() => setStatusFilter(v)}
                          className={statusFilter === v ? 'filter-primary' : 'filter-ghost'}
                          style={{ fontSize: 12, padding: '4px 10px' }}>{label}</button>
                ));
              })()}
            </div>
          )}
          {mode === 'discount' && (
            <button onClick={() => setHideNa(v => !v)}
                    className={hideNa ? 'filter-primary' : 'filter-ghost'}
                    style={{ fontSize: 12, padding: '4px 10px' }}
                    title="반환상태가 '비대상'인 가맹점을 목록에서만 숨깁니다. 데이터·집계·상단 당일 반환 현황은 변경되지 않습니다.">
              {hideNa ? '비대상 숨김 ✓' : '비대상 숨기기'}
            </button>
          )}
          <div style={{ display: 'inline-flex', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--muted)', alignSelf: 'center', marginRight: 4 }}>표시</span>
            {([
              ['all', '전체'],
              ['has_payout', '정산금 있음'],
              ...(mode === 'discount' ? [['has_shortfall', '미차감 발생'] as const, ['expired', '기간 초과'] as const] : []),
            ] as const).map(([v, label]) => (
              <button key={v} onClick={() => setRowFilter(v as any)}
                      className={rowFilter === v ? 'filter-primary' : 'filter-ghost'}
                      style={{ fontSize: 12, padding: '4px 10px' }}>{label}</button>
            ))}
          </div>
          {(q || statusFilter !== 'all' || rowFilter !== 'all') && (
            <button onClick={() => { setQ(''); setStatusFilter('all'); setRowFilter('all'); }}
                    className="filter-ghost" style={{ fontSize: 12, padding: '4px 10px' }}>초기화</button>
          )}
          <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--muted)', alignSelf: 'center', marginRight: 4 }}>표 컬럼</span>
            {([
              ['basic', '기본'],
              ['detail', '상세'],
            ] as const).map(([v, label]) => (
              <button key={v} onClick={() => setViewModePersist(v)}
                      className={viewMode === v ? 'filter-primary' : 'filter-ghost'}
                      style={{ fontSize: 12, padding: '4px 10px' }}
                      title={v === 'basic' ? '핵심 컬럼만 (수수료율·미차감·선납·환수대기 등 숨김)' : '전체 컬럼 표시'}>
                {label}
              </button>
            ))}
          </div>
          {mode === 'discount' && (
            <button onClick={() => setTableExpandOpen(true)}
                    className="filter-ghost"
                    style={{ fontSize: 12, padding: '4px 10px' }}
                    title="상세 컬럼이 많아 조회가 불편할 때 큰 팝업으로 열기 (페이지 폭 90%)">
              확대 조회
            </button>
          )}
          <span style={{ color: 'var(--muted)', fontSize: 12 }}>
            표시 {viewData.length.toLocaleString('ko-KR')}건 / 전체 {data.length.toLocaleString('ko-KR')}건
          </span>
        </div>
        {backfillMsg && <div className="filter-note">{backfillMsg}</div>}
        {err && <div className="error">{err}</div>}
      </div>

      {loading ? (
        <>
          <SkeletonStats count={4} />
          <SkeletonStats count={6} />
          <div className="card" style={{ padding: 0 }}><SkeletonTable rows={6} cols={5} showHeader /></div>
        </>
      ) : viewData.length === 0 ? (
        <div className="card empty-state">
          <ChartColumn className="empty-icon" size={36} strokeWidth={1.6} />
          <h4>해당 일자의 정산 데이터가 없습니다</h4>
          <p>날짜를 변경하거나 결제 수신내역이 있는지 확인해 주세요.</p>
        </div>
      ) : (
        <>
          {mode === 'discount' && (
            <>
              <div className="section-label">당일 반환 현황 — 가맹점별 당일 회수해야 할 양 대비 실제 회수 결과</div>
              <div className="summary" style={{ marginBottom: 10 }}>
                <div className="stat fulfilled">
                  <div className="label"><CircleCheck size={13} /> 반환완료</div>
                  <div className="val">{statusCount('fulfilled').toLocaleString('ko-KR')}</div>
                  <div className="sub">회수해야 할 양 ≤ 실제 회수</div>
                </div>
                <div className="stat partial">
                  <div className="label"><TriangleAlert size={13} /> 일부반환</div>
                  <div className="val">{statusCount('partial').toLocaleString('ko-KR')}</div>
                  <div className="sub">일부만 회수, 미차감 발생</div>
                </div>
                <div className="stat missed">
                  <div className="label"><CircleX size={13} /> 미반환</div>
                  <div className="val">{statusCount('missed').toLocaleString('ko-KR')}</div>
                  <div className="sub">회수 대상이나 0원</div>
                </div>
                <div className="stat na">
                  <div className="label"><CircleMinus size={13} /> 비대상</div>
                  <div className="val">{statusCount('na').toLocaleString('ko-KR')}</div>
                  <div className="sub">오늘 회수 의무 없음</div>
                </div>
              </div>
            </>
          )}

          <div className="section-label">가맹점·매출·정산 합계</div>
          <div className="summary summary-val-align">
            <div className="stat"><div className="label">정산 가맹점</div><div className="val">{viewData.length}</div></div>
            <div className="stat">
              {/* 상세 버튼은 제목 줄 우측 — 금액 줄에 두면 좁은 카드에서 '상/세'로 줄바꿈됨 */}
              <div className="label" style={{ display: 'flex', width: '100%' }}>
                <span>
                  가맹점 매출 <span style={{ whiteSpace: 'nowrap' }}>(합계)<InfoTip text="조회기간 승인·취소를 반영한 매출 합계" /></span>
                </span>
                <button
                  type="button"
                  className="filter-ghost"
                  style={{ padding: '2px 8px', fontSize: 11, fontWeight: 600, lineHeight: 1.4, marginLeft: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
                  onClick={() => setSalesDetailOpen(true)}
                >상세</button>
              </div>
              <div className="val" style={{ whiteSpace: 'nowrap' }}>{fmt.won(sum('discount_noti_paid_gross') + sum('discount_noti_cancel_gross'))}</div>
            </div>
            <div className="stat">
              <div className="label">
                <span>가맹점 매출<br /><span style={{ whiteSpace: 'nowrap' }}>(정산 입력 / 순매출)</span></span>
                <InfoTip text="실제 정산 계산에 반영되는 매출 금액" />
              </div>
              <div className="val">{fmt.won(sum('gross_sales'))}</div>
            </div>
            <div className="stat">
              <div className="label">
                수수료 합계
                <InfoTip text="정산 대상 매출에 수수료율을 적용한 합계" />
              </div>
              <div className="val">{fmt.won(sum('fee_amount'))}</div>
            </div>
            <div className="stat">
              <div className="label">
                총지급액
                <InfoTip text="수수료 등을 반영한 가맹점 지급 기준 금액" />
              </div>
              <div className="val">{fmt.won(sum('net_sales'))}</div>
            </div>
            <div className="stat accent-leaf">
              <div className="label">
                정산금 합계
                <InfoTip text="현재 조회된 가맹점들의 최종 정산금 합계" />
              </div>
              <div className="val">{fmt.won(sum('settle_amount'))}</div>
            </div>
            {mode === 'discount' && <div className="stat">
              <div className="label">
                일차감액
                <InfoTip text="매입액 회수를 위해 총지급액에서 매일 차감할 목표 금액." />
              </div>
              <div className="val">{fmt.won(sum('daily_deduct'))}</div>
            </div>}
            {mode === 'discount' && <div className="stat" style={{ borderLeft: '4px solid #ea580c' }}>
              <div className="label">
                전일미차감 합계
                <InfoTip text="전일까지 이월된 미차감의 합. 오늘 회수해야 할 대상. 필터 적용 시 조회 결과 기준." />
              </div>
              <div className="val" style={{ color: sum('prev_shortfall') > 0 ? '#c2410c' : undefined }}>
                {fmt.won(sum('prev_shortfall'))}
              </div>
              <div className="sub" style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                {isFiltered
                  ? `현재 조회 기준 (전체 ${fmt.won(sumAll('prev_shortfall'))})`
                  : '전체 기준'}
              </div>
            </div>}
            {mode === 'discount' && <div className="stat">
              <div className="label">
                금일 정산 예정액
                <InfoTip text="일차감 회수 후 오늘 가맹점에 지급할 예정인 금액." />
              </div>
              <div className="val">{fmt.won(sum('merchant_payout'))}</div>
            </div>}
            {mode === 'discount' && (
              <div className="stat accent-leaf">
                <div className="label" title="할인매매정산 결제 수신내역 매출 중 실제 회수된 금액.">원금 반환</div>
                <div className="val">{fmt.won(sum('principal_repaid'))}</div>
              </div>
            )}
          </div>

          {tableExpandOpen && (
            <div onClick={() => setTableExpandOpen(false)}
                 style={{
                   position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
                   zIndex: 999,
                 }} />
          )}
          <div className="card"
               style={tableExpandOpen ? {
                 padding: 0,
                 position: 'fixed', top: '5vh', left: '5vw', right: '5vw', bottom: '5vh',
                 zIndex: 1000, background: 'var(--surface)', overflow: 'hidden',
                 display: 'flex', flexDirection: 'column',
                 boxShadow: '0 24px 60px -22px rgba(15,23,42,.35)',
               } : { padding: 0 }}>
            {tableExpandOpen && (
              <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--line)',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: 'var(--gray-50)' }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>정산 표 확대 조회 · {date}</div>
                <button onClick={() => setTableExpandOpen(false)}
                        className="filter-ghost" style={{ fontSize: 12, padding: '4px 10px' }}>
                  닫기
                </button>
              </div>
            )}
            {mode === 'discount' && (
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
                <div className="muted" style={{ fontSize: 12 }}>
                  원금 반환 ₩ 컬럼이 0보다 크면 클릭하여 어떤 할인매매정산 결제 수신내역에서 회수되었는지 상세 확인 가능.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                  {selectedRows.length > 0 ? (
                    <>
                      <div style={{ fontSize: 13, color: 'var(--ink)' }}>
                        선택 <b style={{ color: 'var(--leaf-700)' }}>{selectedRows.length}</b>개 / 합계 <b style={{ color: 'var(--leaf-700)' }}>{fmt.won(selectedSum)}</b>
                      </div>
                      <button onClick={copyAccounts} className="filter-ghost" style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Clipboard size={13} /> 계좌·금액 복사
                      </button>
                      <button onClick={openBatchConfirm} disabled={batchRunning} className="filter-primary" style={{ fontSize: 13 }}>
                        {batchRunning ? '처리 중…' : `일괄 이체 완료 (${selectedRows.length}건)`}
                      </button>
                      <button onClick={() => setSelected(new Set())} className="filter-ghost" style={{ fontSize: 12 }}>선택 해제</button>
                    </>
                  ) : (
                    <div className="muted" style={{ fontSize: 12 }}>
                      송금할 가맹점 행을 체크하면 일괄 이체·계좌 복사가 가능합니다 (정산금이 있는 행만 선택 가능)
                    </div>
                  )}
                </div>
              </div>
            )}
            {copyMsg && (
              <div className="toast-inline" style={{ padding: '8px 16px', background: 'var(--brand-50)', borderBottom: '1px solid var(--brand-200)', fontSize: 12, color: 'var(--brand-700)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={13} /> {copyMsg}
              </div>
            )}
            <div className="table-wrap" style={{ maxHeight: tableExpandOpen ? 'calc(90vh - 130px)' : '60vh', flex: tableExpandOpen ? 1 : undefined, border: 0, '--stick-1-w': mode === 'discount' ? '140px' : '120px', '--stick-2-w': '180px' } as React.CSSProperties}>
              <table>
                <thead><tr>
                  {mode === 'discount' ? (
                    <th className="nowrap stick stick-1" style={{ width: 140 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {(() => {
                          const candidates = payoutCandidates(viewData);
                          const allSelected = candidates.length > 0 && candidates.every(r => selected.has(r.mcht_id));
                          return (
                            <input type="checkbox"
                                   checked={allSelected}
                                   disabled={candidates.length === 0}
                                   onChange={toggleSelectAll}
                                   title="정산금이 있는 행 전체 선택/해제" />
                          );
                        })()}
                        <span title="가맹점 고유 ID (PG가 부여)">MID</span>
                      </div>
                    </th>
                  ) : (
                    <th className="nowrap stick stick-1" title="가맹점 고유 ID (PG가 부여)">MID</th>
                  )}
                  <th className="stick stick-2 stick-end" title="가맹점 상호">가맹점명</th>
                  {mode === 'realtime' && (
                    <th className="nowrap" title="가맹점 마스터에 등록된 정산방식 (bank=계좌, wallet=지갑)">정산방식</th>
                  )}
                  <th className="nowrap" title="반환=반환 진행 중 / 일반=반환금 없음 / 악성=장기 미회수">분류</th>
                  {mode === 'discount' && <th className="nowrap" title="계약 전체 원금 기준 반환상태(A안): 비대상/미반환/일부반환/반환완료. 실제 반환실적(카드매출 원금반환+은행 반환목적입금)만 인정, 관리자 조정은 제외. 상단 '당일 반환 현황'은 당일 기준으로 별개.">반환상태</th>}
                  {mode === 'discount' && <th className="num nowrap" title="매입액 − 누적 회수액 − 당일 원금 반환. 0이 되면 회수 완료.">당일 잔여액</th>}
                  {mode === 'discount' && !isBasic && <th className="num nowrap" title="약정 종료까지 남은 일수. 0 이하로 내려가면 약정 종료 후 미납일수(예: -3일 = 약정 종료 3일 경과). 반환 완료 시 0.">잔여기간</th>}
                  <th className="nowrap" title={mode === 'realtime' ? '실시간 결제(S10) 수신 건수 — 운영 발신만.' : '할인매매정산(D1~5/W1/M1) 수신 건수 — 운영 발신만.'}>
                    결제 건수
                  </th>
                  <th className="num nowrap" title="결제 수신내역 매출 합계 (승인 + 취소). [상세] 클릭 시 승인/취소/합계와 개별 수신 내역 모달.">가맹점 매출 (합계)</th>
                  {!isBasic && <th className="num nowrap" title="가맹점 마스터에 등록된 수수료율">수수료율</th>}
                  <th className="num nowrap" title="가맹점 매출 − 수수료 (net_sales)">총 지급액</th>
                  {mode === 'discount' && !isBasic && <th className="num nowrap" title="매입액 ÷ 약정 기간. 매일 정해진 회수 목표 금액.">일차감액</th>}
                  {mode === 'discount' && <th className="num nowrap" title="전일까지 회수 못 한 미차감 누적. 오늘 입금·조정이 있으면 감소.">전일미차감</th>}
                  {mode === 'discount' && !isBasic && <th className="num nowrap" title="오늘 회수 가능한 상한 = 일차감액 + 전일미차감">당일 반환액</th>}
                  <th className="num nowrap" title={mode === 'realtime' ? '총지급액 그대로 (실시간 정산은 회수 의무 없음).' : '총지급액 − 당일 반환액. 양수=가맹점에 지급할 금액, 음수=회수 부족.'}>정산금</th>
                  {mode === 'discount' && !isBasic && <th className="num nowrap" title="정산금이 음수일 때 그 절대값. 다음날 전일미차감으로 이월.">당일 미차감액</th>}
                  {mode === 'discount' && !isBasic && <th className="num nowrap" title="어제 정산금이 1만원 미만이라 이월된 금액">전일미지급</th>}
                  {mode === 'discount' && !isBasic && <th className="num nowrap" title="오늘 정산금이 1만원 미만이라 이월된 금액. 다음날 합산.">당일미지급</th>}
                  {mode === 'discount' && !isBasic && SHOW_LEGACY_COLS && <th className="num nowrap" title="취소가 결제·미지급을 초과해 가맹점이 우리에게 돌려줘야 할 금액. 이후 일자 매출 또는 가맹점 입금으로 자동 차감.">환수대기</th>}
                  {mode === 'discount' && <th className="num nowrap" title="오늘 가맹점 계좌로 지급할 예정인 금액 (1만원 이상일 때). 실제 송금 완료와는 별개.">금일 정산 예정액</th>}
                  {mode === 'discount' && <th className="num nowrap" title="카드매출에서 실제 회수된 원금 (엑셀 V열과 동일). 은행 반환 목적 입금은 「반환 목적 입금」 컬럼에 별도 표시.">원금 반환</th>}
                  {mode === 'discount' && !isBasic && SHOW_LEGACY_COLS && <th className="num nowrap" title="전일 종료 시 선납 잔액 + 당일 입금. 일차감에 우선 사용됨.">선납 잔액 (시작)</th>}
                  {mode === 'discount' && !isBasic && <th className="num nowrap" title="담당자가 은행 입금내역 확인 후 원금 반환 목적으로 수동 등록한 in 합계 (이 정산에 실제 반영된 금액 기준). 실제 은행 입금일은 이체 이력에서 별도 확인.">반환 목적 입금</th>}
                  {mode === 'discount' && !isBasic && SHOW_LEGACY_COLS && <th className="num nowrap" title="당일 일차감에 선납에서 차감한 금액">선납 사용</th>}
                  {mode === 'discount' && !isBasic && SHOW_LEGACY_COLS && <th className="num nowrap" title="당일 종료 시점 선납 잔액 (다음날로 이월)">선납 잔액 (종료)</th>}
                  {mode === 'discount' && !isBasic && <th className="num nowrap" title="담당자가 당일 등록한 서포트리→가맹점 이체(out) 금액 합계. 실제 은행 송금 완료가 검증된 금액이라는 뜻은 아님.">송금 등록액</th>}
                  {mode === 'discount' && <th className="nowrap">이체</th>}
                </tr></thead>
                <tbody>
                  {viewData.map(r => {
                    const notiCount = r.discount_noti_count ?? 0;
                    const grossMismatch = notiCount > 0 && r.discount_noti_gross !== r.gross_sales;
                    // 목록 반환상태 = 계약 전체 원금 기준(A안). 상단 '당일 반환 현황'과 별개.
                    const status = repayStatus(r);
                    const real = realRepaid(r);
                    const adjContrib = Number(r.adj_principal_contribution ?? 0);
                    const adjNote = adjContrib > 0 ? ` (관리자 조정 ${fmt.won(adjContrib)} 제외)` : '';
                    const statusTitle =
                      status === 'fulfilled' ? `계약 원금 반환완료 — 당일 잔여액 0원.${adjContrib > 0 ? ` 실제 반환실적 ${fmt.won(real)}, 관리자 조정 ${fmt.won(adjContrib)}은 실적 아님.` : ''}` :
                      status === 'partial' ? `실제 반환실적 ${fmt.won(real)}, 잔여 원금 ${fmt.won(r.remaining_amount)}${adjNote}.` :
                      status === 'missed' ? `실제 원금 반환실적 0원, 잔여 ${fmt.won(r.remaining_amount)}${adjContrib > 0 ? ` (관리자 조정 ${fmt.won(adjContrib)}만 존재 — 실적 아님)` : ''}.` :
                      '반환 계약 대상 아님.';
                    const canSelect = r.merchant_payout > 0;
                    const isSelected = selected.has(r.mcht_id);
                    return (
                      <tr key={r.mcht_id} style={isSelected ? { background: '#f0fdf4' } : undefined}>
                        {mode === 'discount' ? (
                          <td className="nowrap stick stick-1 cell-mono" style={{ background: isSelected ? '#f0fdf4' : undefined }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <input type="checkbox"
                                     checked={isSelected}
                                     disabled={!canSelect}
                                     onChange={() => toggleSelect(r.mcht_id)}
                                     title={canSelect ? '선택' : '정산금이 0이라 선택 불가'} />
                              <span>{r.mcht_id}</span>
                            </div>
                          </td>
                        ) : (
                          <td className="nowrap stick stick-1 cell-mono" style={{ background: isSelected ? '#f0fdf4' : undefined }}>{r.mcht_id}</td>
                        )}
                        <td className="cell-name stick stick-2 stick-end" title={r.merchant_name}
                            style={{ background: isSelected ? '#f0fdf4' : undefined }}>
                          <a onClick={(e) => { e.preventDefault(); setDetailMid(r.mcht_id); }}
                             style={{ cursor: 'pointer' }}>
                            {r.merchant_name}
                          </a>
                        </td>
                        {mode === 'realtime' && (
                          <td className="nowrap">
                            {methodOf(r) === 'wallet'
                              ? <span className="chip" style={{ background: '#eef2ff', color: '#4338ca' }}>지갑</span>
                              : <span className="chip" style={{ background: '#f1f5f9', color: '#334155' }}>계좌</span>}
                          </td>
                        )}
                        <td className="nowrap">{r.category ? <span className={`chip cat-${r.category}`}><span className="dot" />{CATEGORY_KO[r.category] ?? r.category}</span> : '-'}</td>
                        {mode === 'discount' && (
                          <td className="nowrap">
                            {(() => { const Icon = STATUS_ICON[status]; return (
                            <div className={`status-badge ${status}`} title={statusTitle}>
                              <span className="label-row"><Icon size={12} />{STATUS_LABEL[status]}</span>
                            </div>
                            ); })()}
                          </td>
                        )}
                        {mode === 'discount' && (
                          <td className="num nowrap" style={{ cursor: 'pointer' }}
                              title="클릭 — 당일 잔여액 조정·관리자 메모 관리 팝업"
                              onClick={() => openAdjModal(r)}>
                            <span style={{ textDecoration: 'underline dotted', textUnderlineOffset: 2 }}>{fmt.won(r.remaining_amount)}</span>
                            {r.has_date_adjustment && (
                              <span style={{ marginLeft: 4, fontSize: 10, padding: '0 4px', borderRadius: 4, background: '#fef9c3', color: '#854d0e', fontWeight: 700 }}
                                    title="이 정산일에 반영된 관리자 조정이 있습니다. 관리자 조정은 실제 반환실적이 아닙니다.">조정</span>
                            )}
                          </td>
                        )}
                        {mode === 'discount' && !isBasic && (
                          <td className="num nowrap" style={
                            r.remaining_days < 0
                              ? { color: '#7f1d1d', fontWeight: 700, background: '#fee2e2' }
                              : r.remaining_days === 0
                                ? (r.remaining_amount === 0
                                    ? { color: 'var(--leaf-700)', fontWeight: 600 }
                                    : { color: '#854d0e', fontWeight: 700 })
                                : r.remaining_days <= 7
                                  ? { color: '#854d0e', fontWeight: 600 }
                                  : undefined
                          }
                              title={r.remaining_days < 0
                                ? `약정 종료 후 ${-r.remaining_days}일 경과 — 미반환분 ${fmt.won(r.remaining_amount)} 남음`
                                : r.remaining_days === 0 && r.remaining_amount > 0
                                  ? '오늘이 약정 마지막 일 — 미반환분 남음'
                                  : undefined}>
                            {`${r.remaining_days}일`}
                          </td>
                        )}
                        <td className="nowrap noti-source">
                          {notiCount > 0 ? (
                            <div>
                              <div className="count">{notiCount.toLocaleString('ko-KR')}건</div>
                              <div className="breakdown">
                                결제 {r.discount_noti_paid_count}
                                {r.discount_noti_cancel_count > 0 && <> · 취소 {r.discount_noti_cancel_count}</>}
                              </div>
                              <div className="gross">합계 {fmt.won(r.discount_noti_gross)}</div>
                            </div>
                          ) : (
                            <span className="empty" title="이 기간 들어온 할인매매정산 결제 수신내역이 없음 → 매출/반환 모두 0">결제 수신내역 없음</span>
                          )}
                        </td>
                        <td className="num nowrap" title={grossMismatch ? `raw 결제 수신 합계 ${fmt.won(r.discount_noti_gross)} 중 범위 외 제외하여 ${fmt.won(r.gross_sales)} 반영` : '할인매매정산 결제 수신내역 매출 합계 (승인 + 취소). [상세] 클릭 시 모달.'}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span>{fmt.won((r.discount_noti_paid_gross ?? 0) + (r.discount_noti_cancel_gross ?? 0))}</span>
                            {notiCount > 0 && (
                              <button
                                type="button"
                                className="filter-ghost"
                                style={{ padding: '2px 6px', fontSize: 10, lineHeight: 1.3 }}
                                title={`승인 ${fmt.won(r.discount_noti_paid_gross ?? 0)} · 취소 ${fmt.won(r.discount_noti_cancel_gross ?? 0)} — 결제 수신내역 ${notiCount}건 상세`}
                                onClick={(e) => { e.stopPropagation(); openRepayDetail(r); }}
                              >상세</button>
                            )}
                            {grossMismatch && <span style={{ color: 'var(--warn)', fontSize: 11 }}>＊</span>}
                          </div>
                        </td>
                        {!isBasic && <td className="num nowrap">{feeRateOf(r) != null ? fmt.pct(feeRateOf(r)!) : '-'}</td>}
                        <td className="num nowrap">{fmt.won(r.net_sales)}</td>
                        {mode === 'discount' && !isBasic && <td className="num nowrap">{fmt.won(r.daily_deduct)}</td>}
                        {mode === 'discount' && <td className="num nowrap" title={`전일 종료 시점 미차감 잔액 (오늘 회수 대상). 당일 종료 후 잔액: ${fmt.won(r.today_shortfall)}`}>{fmt.won(r.prev_shortfall)}</td>}
                        {mode === 'discount' && !isBasic && <td className="num nowrap">{fmt.won(r.today_cap)}</td>}
                        <td className={`num nowrap ${r.settle_amount < 0 ? 'cell-neg bold' : ''}`} style={{ fontWeight: r.settle_amount !== 0 ? 600 : undefined }}>{fmt.won(r.settle_amount)}</td>
                        {mode === 'discount' && !isBasic && <td className="num nowrap">{fmt.won(r.today_shortfall)}</td>}
                        {mode === 'discount' && !isBasic && <td className="num nowrap">{fmt.won(r.prev_unpaid)}</td>}
                        {mode === 'discount' && !isBasic && <td className="num nowrap">{fmt.won(r.today_unpaid)}</td>}
                        {mode === 'discount' && !isBasic && SHOW_LEGACY_COLS && (
                          <td className="num nowrap" style={{ color: (r.refund_pending ?? 0) > 0 ? 'var(--red-700, #b91c1c)' : undefined, fontWeight: (r.refund_pending ?? 0) > 0 ? 700 : undefined }}>
                            {fmt.won(r.refund_pending ?? 0)}
                          </td>
                        )}
                        {mode === 'discount' && <td className="num nowrap cell-payout">{fmt.won(r.merchant_payout)}</td>}
                        {mode === 'discount' && (() => {
                          // 20260909: 화면 표시는 카드매출 원금 반환 (엑셀 V). 은행 반환 목적 입금은 별도 컬럼.
                          const cardV = cardRepaid(r);
                          return (
                          <td
                            className={`num nowrap repaid-cell ${cardV > 0 ? 'has-repaid' : ''}`}
                            title={cardV > 0
                              ? `카드매출에서 실제 회수된 원금 ${fmt.won(cardV)}. 클릭 — 이 회수에 들어간 할인매매정산 결제 수신내역 ${notiCount}건 상세 보기. (은행 반환 목적 입금은 「반환 목적 입금」 컬럼에 별도 표시)`
                              : (notiCount > 0
                                ? `할인매매정산 결제 수신내역은 ${notiCount}건 있으나 카드 회수 0원`
                                : '이 기간 할인매매정산 결제 수신내역이 없어 카드 회수 불가')}
                            onClick={cardV > 0 ? () => openRepayDetail(r) : undefined}
                          >
                            {fmt.won(cardV)}
                            {cardV > 0 && <ChevronRight size={12} className="arrow" />}
                          </td>
                          );
                        })()}
                        {mode === 'discount' && !isBasic && SHOW_LEGACY_COLS && <td className="num nowrap">{fmt.won(r.prepaid_balance_before ?? 0)}</td>}
                        {mode === 'discount' && !isBasic && (
                          <td className="num nowrap" style={{ color: (r.transfer_in_amount ?? 0) > 0 ? 'var(--leaf-700)' : undefined, fontWeight: (r.transfer_in_amount ?? 0) > 0 ? 700 : undefined }}>
                            {fmt.won(r.transfer_in_amount ?? 0)}
                            {(r.transfer_in_count ?? 0) > 0 && <span style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 4 }}>{r.transfer_in_count}건</span>}
                          </td>
                        )}
                        {mode === 'discount' && !isBasic && SHOW_LEGACY_COLS && <td className="num nowrap">{fmt.won(r.prepaid_used ?? 0)}</td>}
                        {mode === 'discount' && !isBasic && SHOW_LEGACY_COLS && <td className="num nowrap" style={{ fontWeight: (r.prepaid_balance_after ?? 0) > 0 ? 600 : undefined }}>{fmt.won(r.prepaid_balance_after ?? 0)}</td>}
                        {mode === 'discount' && !isBasic && (
                          <td className="num nowrap" style={{ color: (r.transfer_out_amount ?? 0) > 0 ? '#2563eb' : undefined, fontWeight: (r.transfer_out_amount ?? 0) > 0 ? 700 : undefined }}>
                            {fmt.won(r.transfer_out_amount ?? 0)}
                            {(r.transfer_out_count ?? 0) > 0 && <span style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 4 }}>{r.transfer_out_count}건</span>}
                          </td>
                        )}
                        {mode === 'discount' && (
                          <td className="nowrap">
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button onClick={() => openXfer(r, 'in')} className="filter-ghost" style={{ padding: '4px 8px', fontSize: 11 }} title="가맹점이 송금한 금액 등록 (미납 상쇄 + 선납)">입금</button>
                              {r.merchant_payout > 0 && (
                                <button onClick={() => openXfer(r, 'out')} className="filter-ghost" style={{ padding: '4px 8px', fontSize: 11, color: '#2563eb', borderColor: '#bfdbfe' }} title="가맹점에 정산금 송금 완료 처리">이체</button>
                              )}
                              <button onClick={() => openXferLog(r)} className="filter-ghost" style={{ padding: '4px 8px', fontSize: 11 }} title="이체 이력">이력</button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* 일괄 이체 최종 확인 모달 — 대상 가맹점 수·가맹점별 지급금액·총 지급금액 요약 */}
      {batchConfirmOpen && (
        <div className="modal-overlay" onClick={() => !batchRunning && setBatchConfirmOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div className="modal-header">
              <div>
                <div className="subtitle">일괄 이체 최종 확인 — {date}</div>
                <div className="title">
                  대상 {selectedRows.length}건 · 총 지급 {fmt.won(selectedSum)}
                </div>
              </div>
              <button onClick={() => !batchRunning && setBatchConfirmOpen(false)} className="modal-close" disabled={batchRunning}>닫기</button>
            </div>
            <div className="modal-body">
              <div className="callout" style={{ marginBottom: 12 }}>
                아래 가맹점 <b>{selectedRows.length}개</b>에 대해 <b>일괄 이체 완료</b>로 기록합니다 (합계 <b>{fmt.won(selectedSum)}</b>).
                실제 송금은 별도로 진행하셨다는 전제이며, 이력에는 처리자·처리시각·가맹점별 지급금액이 기록됩니다.
              </div>
              <div className="table-wrap" style={{ maxHeight: '48vh' }}>
                <table>
                  <thead><tr>
                    <th className="nowrap">MID</th>
                    <th>가맹점명</th>
                    <th className="nowrap">정산방식</th>
                    <th className="nowrap">계좌 / 지갑</th>
                    <th className="num nowrap">지급금액</th>
                  </tr></thead>
                  <tbody>
                    {selectedRows.map(r => {
                      const method = (r as any).payout_method ?? 'bank';
                      const acct = method === 'wallet'
                        ? ((r as any).people_id ? `피플 ID ${(r as any).people_id}` : '(피플 ID 미입력)')
                        : ((r as any).payout_bank
                            ? `${(r as any).payout_bank} ${(r as any).payout_account ?? ''} (${(r as any).payout_holder ?? '-'})`
                            : '(정산 계좌 미입력)');
                      return (
                        <tr key={r.mcht_id}>
                          <td className="nowrap cell-mono">{r.mcht_id}</td>
                          <td>{r.merchant_name}</td>
                          <td className="nowrap">{method === 'wallet' ? '지갑정산' : '은행정산'}</td>
                          <td className="nowrap" style={{ fontSize: 12, color: 'var(--muted)' }}>{acct}</td>
                          <td className="num nowrap"><b>{fmt.won(r.merchant_payout)}</b></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} className="nowrap" style={{ textAlign: 'right', fontWeight: 700 }}>총 지급금액</td>
                      <td className="num nowrap" style={{ fontWeight: 700, color: 'var(--leaf-700)' }}>{fmt.won(selectedSum)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="filter-ghost" onClick={() => setBatchConfirmOpen(false)} disabled={batchRunning}>취소</button>
                <button className="filter-primary" onClick={runBatchTransferOut} disabled={batchRunning}>
                  {batchRunning ? '처리 중…' : `확인 · ${selectedRows.length}건 이체 완료 기록`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 일괄 이체 결과 요약 */}
      {batchResult && (
        <div className="modal-overlay" onClick={() => setBatchResult(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 620 }}>
            <div className="modal-header">
              <div>
                <div className="subtitle">일괄 이체 결과 — {date}</div>
                <div className="title">
                  총 {batchResult.total}건 · 성공 {batchResult.ok} · 실패 {batchResult.fail.length}
                </div>
              </div>
              <button onClick={() => setBatchResult(null)} className="modal-close">닫기</button>
            </div>
            <div className="modal-body">
              {batchResult.fail.length === 0 ? (
                <div className="callout" style={{ color: 'var(--leaf-700)' }}>
                  선택한 {batchResult.total}건 모두 이체 완료로 기록되었습니다.
                </div>
              ) : (
                <>
                  <div className="callout" style={{ background: '#fef3c7', borderColor: '#fde68a', color: '#854d0e' }}>
                    일부 건 처리 실패 — 아래 항목을 확인하세요.
                  </div>
                  <div className="table-wrap" style={{ maxHeight: '40vh', marginTop: 10 }}>
                    <table>
                      <thead><tr>
                        <th className="nowrap">MID</th>
                        <th>가맹점명</th>
                        <th>오류</th>
                      </tr></thead>
                      <tbody>
                        {batchResult.fail.map(f => (
                          <tr key={f.mid}>
                            <td className="nowrap cell-mono">{f.mid}</td>
                            <td>{f.name}</td>
                            <td style={{ color: '#b91c1c', fontSize: 12 }}>{f.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button className="filter-primary" onClick={() => setBatchResult(null)}>닫기</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {adjModal && (() => {
        const r = adjModal;
        const pv = adjPreview();
        return (
          <div className="modal-overlay" onClick={closeAdjModal}>
            <div className="modal lg" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <div className="subtitle">당일 잔여액 조정 · 메모 — {date}</div>
                  <div className="title">
                    {r.merchant_name} <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 500, marginLeft: 6 }}>({r.mcht_id})</span>
                  </div>
                </div>
                <button onClick={closeAdjModal} className="modal-close">닫기</button>
              </div>
              <div className="modal-body">
                {/* 4-1 현재 정보 */}
                <div className="summary" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', marginBottom: 16 }}>
                  <div className="stat"><div className="label">정산 기준일</div><div className="val" style={{ fontSize: 16 }}>{date}</div></div>
                  <div className="stat"><div className="label">현재 당일 잔여액</div><div className="val" style={{ fontSize: 16 }}>{fmt.won(r.remaining_amount)}</div></div>
                </div>

                {/* 4-2 잔여액 조정 */}
                <div className="section-label">잔여액 조정 (관리자 조정 — 실제 반환실적 아님)</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'inline-flex', gap: 4 }}>
                    <button className={adjSign === '+' ? 'filter-primary' : 'filter-ghost'} style={{ padding: '4px 12px' }} onClick={() => setAdjSign('+')}>가산 (+)</button>
                    <button className={adjSign === '-' ? 'filter-primary' : 'filter-ghost'} style={{ padding: '4px 12px' }} onClick={() => setAdjSign('-')}>차감 (−)</button>
                  </div>
                  <input type="text" inputMode="numeric" value={adjAmount}
                         onChange={e => setAdjAmount(e.target.value.replace(/[^\d]/g, ''))}
                         placeholder="조정금액" style={{ width: 140 }} />
                  <input type="text" value={adjReason} onChange={e => setAdjReason(e.target.value)}
                         placeholder="조정 사유 (예: 소송비용 가산, 협의 감액)" style={{ flex: 1, minWidth: 220 }} />
                </div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
                  조정 전 {fmt.won(pv.before)} → 조정 후 예상 <b style={{ color: 'var(--text)' }}>{fmt.won(pv.after)}</b>
                  {pv.delta !== 0 && <> ({pv.delta > 0 ? '+' : ''}{fmt.won(pv.delta)})</>}
                </div>
                {adjErr && <div className="error" style={{ marginBottom: 8 }}>{adjErr}</div>}
                {adjMsg && <div className="filter-note" style={{ marginBottom: 8 }}>{adjMsg}</div>}
                <button className="filter-primary" style={{ padding: '6px 14px', marginBottom: 16 }}
                        disabled={adjSaving} onClick={submitAdj}>
                  {adjSaving ? '저장 중…' : '잔여액 조정 저장'}
                </button>

                {/* 4-3 관리자 메모 (가맹점 관리 메모 — 금액 불변) */}
                <div className="section-label">관리자 메모 (가맹점 메모 — 저장해도 당일 잔여액 변경 없음)</div>
                <textarea value={adjMerchantMemo} onChange={e => setAdjMerchantMemo(e.target.value)}
                          rows={3} style={{ width: '100%', marginBottom: 8 }}
                          placeholder="예: 전일미차감 0원 처리 후 잔여액 가산. 실제 반환 입금 아님." />
                {adjMemoMsg && <div className="filter-note" style={{ marginBottom: 8 }}>{adjMemoMsg}</div>}
                <button className="filter-ghost" style={{ padding: '6px 14px', marginBottom: 16 }}
                        disabled={adjMemoSaving} onClick={submitAdjMemo}>
                  {adjMemoSaving ? '저장 중…' : '메모만 저장'}
                </button>

                {/* 4-4 조정 이력 */}
                <div className="section-label">조정 이력</div>
                {adjHistLoading ? <div style={{ color: 'var(--muted)', fontSize: 13 }}>불러오는 중…</div> : (
                  (adjHistory && adjHistory.length > 0) ? (
                    <div className="card" style={{ padding: 0, maxHeight: 240, overflow: 'auto' }}>
                      <table className="table"><thead><tr>
                        <th className="nowrap">처리일</th><th className="nowrap">구분</th>
                        <th className="num nowrap">금액</th><th>사유</th><th className="nowrap">담당자</th>
                      </tr></thead><tbody>
                        {adjHistory.map(a => (
                          <tr key={a.id}>
                            <td className="nowrap">{a.effective_date}</td>
                            <td className="nowrap">{ADJ_KIND_LABEL[a.kind] ?? a.kind}</td>
                            <td className="num nowrap" style={{ color: a.amount < 0 ? '#b91c1c' : undefined }}>
                              {a.amount > 0 ? '+' : ''}{fmt.won(a.amount)}
                            </td>
                            <td style={{ whiteSpace: 'pre-wrap' }}>{a.reason ?? '-'}</td>
                            <td className="nowrap">{a.created_by ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody></table>
                    </div>
                  ) : <div style={{ color: 'var(--muted)', fontSize: 13 }}>조정 이력이 없습니다.</div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {repayDetail && (() => {
        const r = repayDetail;
        const status = repayStatus(r);
        const cardV = cardRepaid(r);
        const fulfillRate = r.today_cap > 0 ? Math.min(100, Math.round((cardV / r.today_cap) * 100)) : null;
        const paidCount = (repayNotis ?? []).filter(n => !n.is_cancel).length;
        const cancelCount = (repayNotis ?? []).filter(n => !!n.is_cancel).length;
        const sumGross = (repayNotis ?? []).reduce((s, n) => s + (Number(n.gross_amount) || 0), 0);
        const sumFee = (repayNotis ?? []).reduce((s, n) => s + (Number(n.fee_amount) || 0), 0);
        const sumNet = (repayNotis ?? []).reduce((s, n) => s + (Number(n.net_amount) || 0), 0);
        return (
          <div className="modal-overlay" onClick={closeRepayDetail}>
            <div className={`modal ${repayDetailExpanded ? 'xl' : 'lg'}`} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <div className="subtitle">반환 상세 — {date}</div>
                  <div className="title">
                    {r.merchant_name} <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 500, marginLeft: 6 }}>({r.mcht_id})</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button onClick={() => setRepayDetailExpanded(v => !v)}
                          className="filter-ghost" style={{ padding: '4px 10px', fontSize: 12 }}
                          title="상세 팝업 크기 전환 (90% 뷰포트)">
                    {repayDetailExpanded ? '축소' : '확대'}
                  </button>
                  <button onClick={closeRepayDetail} className="modal-close">닫기</button>
                </div>
              </div>
              <div className="modal-body">
                <div className="summary" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', marginBottom: 16 }}>
                  <div className={`stat ${status}`} title="계약 전체 원금 기준 반환상태(A안)">
                    <div className="label">반환상태</div>
                    <div className="val" style={{ fontSize: 16 }}>
                      {STATUS_LABEL[status]}
                    </div>
                  </div>
                  <div className="stat accent-leaf" title="카드매출에서 실제 회수된 원금 (엑셀 V열과 동일). 은행 반환 목적 입금은 이 값에 포함되지 않으며 별도로 표시됩니다.">
                    <div className="label">원금 반환</div>
                    <div className="val" style={{ fontSize: 16 }}>{fmt.won(cardV)}</div>
                  </div>
                  <div className="stat"
                       title="반환 목적 입금과 실제 원금 잔여를 반영한 후 카드매출에서 추가로 회수할 금액.">
                    <div className="label">당일 반환액(목표)</div>
                    <div className="val" style={{ fontSize: 16 }}>{fmt.won(r.today_cap)}</div>
                    <div className="sub">반환 입금·원금 잔여 반영 후 카드 회수 목표</div>
                  </div>
                  <div className="stat">
                    <div className="label">당일 미차감</div>
                    <div className={`val ${r.today_shortfall > 0 ? 'cell-neg' : ''}`} style={{ fontSize: 16 }}>{fmt.won(r.today_shortfall)}</div>
                  </div>
                  <div className="stat"><div className="label">가맹점 매출 (정산 입력)</div><div className="val" style={{ fontSize: 16 }}>{fmt.won(r.gross_sales)}</div></div>
                  <div className="stat"><div className="label">총 지급액</div><div className="val" style={{ fontSize: 16 }}>{fmt.won(r.net_sales)}</div></div>
                </div>

                <div className="muted" style={{ marginBottom: 8 }}>
                  매출집계 범위 <b>{r.range_start} ~ {r.range_end}</b> 동안 들어온 <b style={{ color: 'var(--leaf-700)' }}>할인매매 결제 수신내역</b> 전체 (운영 기준<span title="기술 필드명: is_test=0" style={{ color: 'var(--muted)', fontSize: 11, marginLeft: 4 }}>ⓘ</span>).
                  이 수신내역의 매출에서 위 원금 반환이 회수되었습니다.
                </div>

                {repayLoading ? (
                  <div className="loading">결제 수신내역 조회 중</div>
                ) : repayErr ? (
                  <div className="error">{repayErr}</div>
                ) : !repayNotis || repayNotis.length === 0 ? (
                  <div className="empty-state">
                    <Inbox className="empty-icon" size={36} strokeWidth={1.6} />
                    <p>이 범위에 매칭되는 할인매매 결제 수신내역이 없습니다.</p>
                  </div>
                ) : (
                  <>
                    <div className="noti-totals">
                      <div><b>{repayNotis.length}건</b> <span style={{ color: 'var(--muted)' }}>(결제 {paidCount} · 취소 {cancelCount})</span></div>
                      <div>매출 합 <b>{fmt.won(sumGross)}</b></div>
                      <div>수수료 합 <b>{fmt.won(sumFee)}</b></div>
                      <div>총지급 합 <b>{fmt.won(sumNet)}</b></div>
                    </div>
                    <div className="table-wrap" style={{ maxHeight: '50vh' }}>
                      <table>
                        <thead><tr>
                          <th className="nowrap">발생시각</th>
                          <th className="nowrap">취소시각</th>
                          <th className="nowrap">취소</th>
                          <th className="nowrap" title="기술 필드명: settlmnt_cycle">정산주기</th>
                          <th className="num nowrap">매출액</th>
                          <th className="num nowrap">수수료율</th>
                          <th className="num nowrap">수수료</th>
                          <th className="num nowrap">순액</th>
                          <th className="nowrap">승인번호</th>
                          <th className="nowrap">주문번호</th>
                        </tr></thead>
                        <tbody>
                          {repayNotis.map(n => (
                            <tr key={n.ord_num}>
                              <td className="nowrap cell-mono" style={{ color: 'var(--ink)' }}>{n.trx_at}</td>
                              <td className="nowrap cell-mono">{n.cxl_at ?? '-'}</td>
                              <td className="nowrap">{n.is_cancel ? <span className="cancel-tag">취소</span> : '-'}</td>
                              <td className="nowrap cell-mono">{n.settlmnt_cycle ?? '-'}</td>
                              <td className={`num nowrap ${n.gross_amount < 0 ? 'cell-neg' : ''}`} style={{ fontWeight: 600 }}>{fmt.won(n.gross_amount)}</td>
                              <td className="num nowrap">{n.fee_rate != null ? (n.fee_rate * 100).toFixed(2) + '%' : '-'}</td>
                              <td className="num nowrap">{fmt.won(n.fee_amount)}</td>
                              <td className="num nowrap">{fmt.won(n.net_amount)}</td>
                              <td className="nowrap cell-mono">{n.appr_num ?? '-'}</td>
                              <td className="nowrap cell-mono">{n.ord_num}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* 이체 등록 모달 */}
      {xferModal && (
        <div className="modal-overlay" onClick={closeXfer}>
          <div className="modal sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="subtitle">{xferModal.direction === 'in' ? '입금 등록 (가맹점 → 서포트리)' : '이체 완료 처리 (서포트리 → 가맹점)'}</div>
                <div className="title">{xferModal.row.merchant_name} <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 500, marginLeft: 6 }}>({xferModal.row.mcht_id})</span></div>
              </div>
              <button onClick={closeXfer} className="modal-close">닫기</button>
            </div>
            <div className="modal-body">
              {xferModal.direction === 'in' ? (
                <div className="muted" style={{ marginBottom: 14, lineHeight: 1.6 }}>
                  현재 누적 미납 <b style={{ color: 'var(--warn)' }}>{fmt.won((xferModal.row.prev_shortfall ?? 0) + (xferModal.row.today_shortfall ?? 0))}</b>,
                  현재 선납 잔액 <b style={{ color: 'var(--leaf-700)' }}>{fmt.won(xferModal.row.prepaid_balance_after ?? 0)}</b>.<br/>
                  입금액은 누적 미납을 우선 상쇄하고, 남는 금액은 선납 잔액으로 보관됩니다 (다음날 일차감에 우선 사용).
                </div>
              ) : (
                <div className="muted" style={{ marginBottom: 14, lineHeight: 1.6 }}>
                  당일 가맹점 정산 예정액 <b style={{ color: 'var(--leaf-700)' }}>{fmt.won(xferModal.row.merchant_payout)}</b>.
                  실제 송금하신 금액을 입력하시면 이체 완료 로그로 기록됩니다 (정산 수치엔 영향 없음).
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {xferModal.direction === 'in' ? (
                  <>
                    <label className="filter-field">실제 은행 입금일 (trx_date)
                      <input type="date" value={xferDate} onChange={e => setXferDate(e.target.value)} />
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        가맹점이 회사 계좌에 실제 입금한 날짜. 기본값: 오늘(KST).
                      </div>
                    </label>
                    <label className="filter-field">정산 반영일 (ref_settle_date)
                      <input type="date" value={xferRefSettleDate} onChange={e => setXferRefSettleDate(e.target.value)} />
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        이 반환 입금을 반영할 정산기준일. 매출집계일(D-1)이 아니라 화면 상단 <b>정산기준일</b>입니다. 기본값: 현재 조회 정산일.
                      </div>
                    </label>
                  </>
                ) : (
                  <label className="filter-field">이체 일자
                    <input type="date" value={xferDate} onChange={e => setXferDate(e.target.value)} />
                  </label>
                )}
                <label className="filter-field">금액 (원)
                  <input type="number" min={1} step={1} value={xferAmount}
                         onChange={e => setXferAmount(e.target.value)} placeholder="예: 100000" />
                </label>
                <label className="filter-field">메모 (선택)
                  <input type="text" value={xferMemo} onChange={e => setXferMemo(e.target.value)} placeholder="이체 사유, 송금자 등" />
                </label>
                {/* 문서 09-01 §7: in/out 둘 다 증빙 첨부 지원 */}
                {(xferModal.direction === 'in' || xferModal.direction === 'out') && (
                  <div className="filter-field">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>{xferModal.direction === 'in' ? '입금' : '이체'} 증빙 첨부 (선택)</span>
                      <span style={{ color: 'var(--muted)', fontSize: 11 }}>
                        JPG/PNG · 파일당 최대 5MB · 최대 {MAX_ATT_PER_TRANSFER}장
                      </span>
                    </div>
                    <input type="file" accept="image/jpeg,image/png" multiple
                           disabled={xferFiles.length >= MAX_ATT_PER_TRANSFER}
                           onChange={e => { onPickFiles(e.target.files); e.target.value = ''; }} />
                    {xferFiles.length > 0 && (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {xferFiles.map((f, idx) => (
                          <div key={idx} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '6px 10px', background: 'var(--surface-alt, #fafbfa)',
                            border: '1px solid var(--border, #e6ece9)', borderRadius: 6,
                            fontSize: 12,
                          }}>
                            <span style={{ flex: 1, wordBreak: 'break-all' }}>{f.name}</span>
                            <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                              {(f.size / 1024).toFixed(0)}KB
                            </span>
                            <button type="button" onClick={() => removePickedFile(idx)}
                                    className="filter-ghost" style={{ padding: '2px 8px', fontSize: 11 }}>제거</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {xferErr && <div className="error" style={{ marginTop: 10 }}>{xferErr}</div>}
              <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={closeXfer} className="filter-ghost">취소</button>
                <button onClick={submitXfer} disabled={xferSaving} className="filter-primary">
                  {xferSaving ? '저장 중…' : (xferModal.direction === 'in' ? '입금 등록' : '이체 완료 처리')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 이체 이력 모달 */}
      {xferLogMcht && (
        <div className="modal-overlay" onClick={closeXferLog}>
          <div className="modal lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="subtitle">이체 이력</div>
                <div className="title">{xferLogMcht.merchant_name} <span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 500, marginLeft: 6 }}>({xferLogMcht.mcht_id})</span></div>
              </div>
              <button onClick={closeXferLog} className="modal-close">닫기</button>
            </div>
            <div className="modal-body">
              {xferLogLoading ? (
                <div className="loading">조회 중</div>
              ) : !xferLog || xferLog.length === 0 ? (
                <div className="empty-state">
                  <Inbox className="empty-icon" size={36} strokeWidth={1.6} />
                  <p>이 가맹점에 등록된 이체 이력이 없습니다.</p>
                </div>
              ) : (
                <div className="table-wrap" style={{ maxHeight: '60vh' }}>
                  <table>
                    <thead><tr>
                      <th className="nowrap" title="실제 은행에서 자금이 이동한 날짜">거래일<br/><span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>(trx_date)</span></th>
                      <th className="nowrap" title="이 거래가 반영되는 정산기준일 (in=반환 정산일, out=지급 정산일)">정산 반영일<br/><span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>(ref_settle_date)</span></th>
                      <th className="nowrap">방향</th>
                      <th className="num nowrap">금액</th>
                      <th>메모</th>
                      <th className="nowrap">증빙</th>
                      <th className="nowrap">등록자</th>
                      <th className="nowrap">등록시각</th>
                      <th style={{ minWidth: 64 }}></th>
                    </tr></thead>
                    <tbody>
                      {xferLog.map(x => {
                        const attCnt = x.attachment_count ?? 0;
                        const isOpen = expandedAtt.has(x.id);
                        const atts = attByTransfer[x.id];
                        return (
                          <React.Fragment key={x.id}>
                            <tr>
                              <td className="nowrap cell-mono" style={{ color: 'var(--ink)' }}>{x.trx_date}</td>
                              <td className="nowrap cell-mono" style={{ color: x.ref_settle_date ? 'var(--ink)' : 'var(--muted)' }}>
                                {x.ref_settle_date ?? '-'}
                              </td>
                              <td className="nowrap" style={{ fontWeight: 600, color: x.direction === 'in' ? 'var(--leaf-700)' : '#2563eb' }}>
                                {x.direction === 'in' ? '입금 (가맹점→서포트리)' : '송금 (서포트리→가맹점)'}
                              </td>
                              <td className="num nowrap" style={{ fontWeight: 700 }}>{fmt.won(x.amount)}</td>
                              <td>{x.memo ?? '-'}</td>
                              <td className="nowrap">
                                {attCnt > 0 ? (
                                  <button onClick={() => toggleAttachments(x.id)}
                                          className="filter-ghost"
                                          style={{ padding: '2px 8px', fontSize: 11 }}
                                          title="첨부 미리보기 열기/닫기">
                                    📎 {attCnt}장 {isOpen ? '접기' : '보기'}
                                  </button>
                                ) : (
                                  <span style={{ color: 'var(--muted)', fontSize: 12 }}>-</span>
                                )}
                              </td>
                              <td className="nowrap cell-mono">{x.created_by ?? '-'}</td>
                              <td className="nowrap cell-mono">{x.created_at}</td>
                              <td className="nowrap" style={{ minWidth: 64, textAlign: 'center' }}>
                                <button onClick={() => deleteXfer(x.id)} className="filter-ghost"
                                        style={{ padding: '4px 8px', fontSize: 11, color: 'var(--warn)', whiteSpace: 'nowrap', minWidth: 44 }}>삭제</button>
                              </td>
                            </tr>
                            {isOpen && (
                              <tr key={`att-${x.id}`}>
                                <td colSpan={9} style={{ background: 'var(--surface-alt, #fafbfa)', padding: '10px 14px' }}>
                                  {!atts ? (
                                    <div className="muted" style={{ fontSize: 12 }}>불러오는 중…</div>
                                  ) : atts.length === 0 ? (
                                    <div className="muted" style={{ fontSize: 12 }}>첨부 없음</div>
                                  ) : (
                                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                      {atts.map(a => {
                                        const url = `${API_BASE}/transfers/${x.id}/attachments/${a.id}`;
                                        return (
                                          <div key={a.id} style={{
                                            border: '1px solid var(--border, #e6ece9)', borderRadius: 6,
                                            padding: 8, background: '#fff', width: 180, fontSize: 11,
                                          }}>
                                            <a href="#" onClick={async (e) => {
                                              e.preventDefault();
                                              const tok = getToken();
                                              const r = await fetch(url, { headers: tok ? { Authorization: `Bearer ${tok}` } : undefined });
                                              if (!r.ok) { alert('첨부 열기 실패'); return; }
                                              const b = await r.blob();
                                              const u = URL.createObjectURL(b);
                                              window.open(u, '_blank');
                                              setTimeout(() => URL.revokeObjectURL(u), 60_000);
                                            }}>
                                              <AuthImg url={url} alt={a.filename}
                                                       style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 4, background: '#f3f4f6' }} />
                                            </a>
                                            <div style={{ marginTop: 6, wordBreak: 'break-all' }}>{a.filename}</div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--muted)', marginTop: 4 }}>
                                              <span>{(a.byte_size / 1024).toFixed(0)}KB</span>
                                              <button onClick={() => deleteAttachment(x.id, a.id)}
                                                      className="filter-ghost"
                                                      style={{ padding: '1px 6px', fontSize: 10, color: 'var(--warn)' }}>삭제</button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <MerchantDetailModal mid={detailMid} open={!!detailMid} onClose={() => setDetailMid(null)} />

      {/* 전체 가맹점 매출 상세 — 푸터 [가맹점 매출 (합계)] [상세] 버튼에서 진입 */}
      {salesDetailOpen && (() => {
        const totalPaid = sum('discount_noti_paid_gross');
        const totalCancel = sum('discount_noti_cancel_gross');
        const totalSum = totalPaid + totalCancel;
        const totalCount = sum('discount_noti_count');
        const totalPaidCnt = sum('discount_noti_paid_count');
        const totalCancelCnt = sum('discount_noti_cancel_count');
        // 가맹점별 — 노티가 있는 행만, 합계 큰 순
        const rows = [...data]
          .filter(r => (r.discount_noti_count ?? 0) > 0)
          .map(r => ({
            mid: r.mcht_id,
            name: r.merchant_name,
            count: r.discount_noti_count ?? 0,
            paid_count: r.discount_noti_paid_count ?? 0,
            cancel_count: r.discount_noti_cancel_count ?? 0,
            paid: r.discount_noti_paid_gross ?? 0,
            cancel: r.discount_noti_cancel_gross ?? 0,
            total: (r.discount_noti_paid_gross ?? 0) + (r.discount_noti_cancel_gross ?? 0),
          }))
          .sort((a, b) => b.total - a.total);
        return (
          <div className="modal-overlay" onClick={() => setSalesDetailOpen(false)}>
            <div className={`modal ${salesDetailExpanded ? 'xl' : 'lg'}`} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <div className="subtitle">가맹점 매출 상세 — {data[0]?.range_start ?? date} ~ {data[0]?.range_end ?? date}</div>
                  <div className="title">가맹점 매출 상세 (운영 결제 기준)</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button onClick={() => setSalesDetailExpanded(v => !v)}
                          className="filter-ghost" style={{ padding: '4px 10px', fontSize: 12 }}
                          title="상세 팝업 크기 전환">{salesDetailExpanded ? '축소' : '확대'}</button>
                  <button onClick={() => setSalesDetailOpen(false)} className="modal-close">닫기</button>
                </div>
              </div>
              <div className="modal-body">
                <div className="summary" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', marginBottom: 16 }}>
                  <div className="stat">
                    <div className="label">결제 수신 건수</div>
                    <div className="val" style={{ fontSize: 16 }}>{totalCount.toLocaleString('ko-KR')}건</div>
                    <div className="sub">승인 {totalPaidCnt} · 취소 {totalCancelCnt}</div>
                  </div>
                  <div className="stat">
                    <div className="label">승인 매출액</div>
                    <div className="val" style={{ fontSize: 16 }}>{fmt.won(totalPaid)}원</div>
                  </div>
                  <div className="stat">
                    <div className="label">취소 금액</div>
                    <div className="val" style={{ fontSize: 16, color: totalCancel < 0 ? 'var(--red-600)' : undefined }}>{fmt.won(totalCancel)}원</div>
                  </div>
                  <div className="stat accent-leaf">
                    <div className="label">최종 반영 매출액</div>
                    <div className="val" style={{ fontSize: 16 }}>{fmt.won(totalSum)}원</div>
                  </div>
                </div>

                {rows.length === 0 ? (
                  <div className="empty-state">
                    <Inbox className="empty-icon" size={36} strokeWidth={1.6} />
                    <p>이 범위에 들어온 할인매매 결제 수신이 없습니다.</p>
                  </div>
                ) : (
                  <div className="table-wrap" style={{ maxHeight: '55vh' }}>
                    <table>
                      <thead><tr>
                        <th className="nowrap">MID</th>
                        <th>가맹점명</th>
                        <th className="num nowrap">수신 건수</th>
                        <th className="num nowrap">승인 매출액</th>
                        <th className="num nowrap">취소 금액</th>
                        <th className="num nowrap">최종 반영</th>
                      </tr></thead>
                      <tbody>
                        {rows.map(r => (
                          <tr key={r.mid}>
                            <td className="nowrap cell-mono" style={{ color: 'var(--muted)' }}>{r.mid}</td>
                            <td style={{ fontWeight: 600 }}>{r.name}</td>
                            <td className="num nowrap">
                              {r.count}건
                              <div className="muted" style={{ fontSize: 11 }}>결제 {r.paid_count} · 취소 {r.cancel_count}</div>
                            </td>
                            <td className="num nowrap">{fmt.won(r.paid)}</td>
                            <td className="num nowrap" style={{ color: r.cancel < 0 ? 'var(--red-600)' : 'var(--text-faint)' }}>
                              {r.cancel !== 0 ? fmt.won(r.cancel) : '-'}
                            </td>
                            <td className="num nowrap" style={{ fontWeight: 700 }}>{fmt.won(r.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
